import JsSIP from "jssip";
import io from "socket.io-client";
import Swal from "sweetalert2";
import Vue from "vue";
import axios from "axios";

// Enable JsSIP debugging to see WebSocket traffic
JsSIP.debug.enable("JsSIP:*");

// Non-reactive storage for JsSIP objects to avoid proxy issues
let userAgentInstance = null;
let sessionInstance = null;
let incomingSessionInstance = null;
let callTimer = null;
let incomingAudioElement = null;
let socketInstance = null;
let joinedCompanyId = null;
let audioContextResumed = false;
let userInteractionOccurred = false;
let pendingAudioPlays = [];

// Detect if we're on a mobile browser
function isMobileBrowser() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
}

// Resume AudioContext if suspended (required for mobile browsers)
async function ensureAudioContextResumed() {
  if (audioContextResumed) return true;

  try {
    // Try to resume any existing AudioContext
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (AudioContext) {
      const ctx = new AudioContext();
      if (ctx.state === "suspended") {
        await ctx.resume();
        console.log("🔊 [MOBILE] AudioContext resumed from suspended state");
      }
      audioContextResumed = true;
      return true;
    }
  } catch (e) {
    console.warn("🔊 [MOBILE] Could not resume AudioContext:", e.message);
  }
  return false;
}

// Global user interaction handler for mobile audio unlock
function handleUserInteraction() {
  if (userInteractionOccurred) return;
  userInteractionOccurred = true;
  console.log("🔊 [MOBILE] User interaction detected - unlocking audio");

  // Resume AudioContext
  ensureAudioContextResumed();

  // Try to play any pending audio elements
  if (pendingAudioPlays.length > 0) {
    console.log(
      `🔊 [MOBILE] Attempting to play ${pendingAudioPlays.length} pending audio elements`,
    );
    pendingAudioPlays.forEach(async (audioEl) => {
      if (audioEl && audioEl.srcObject && audioEl.paused) {
        try {
          audioEl.volume = 1.0;
          audioEl.muted = false;
          await audioEl.play();
          console.log(
            `🔊 [MOBILE] ✅ Pending audio now playing: ${audioEl.id ||
              "unnamed"}`,
          );
        } catch (e) {
          console.warn(`🔊 [MOBILE] Still couldn't play: ${e.message}`);
        }
      }
    });
    pendingAudioPlays = [];
  }
}

// Register global interaction listener (one-time setup)
function setupMobileAudioUnlock() {
  if (typeof document === "undefined") return;

  const events = ["touchstart", "touchend", "click", "keydown"];
  const options = { once: false, passive: true };

  events.forEach((event) => {
    document.addEventListener(event, handleUserInteraction, options);
  });

  console.log("🔊 [MOBILE] Audio unlock listeners registered");
}

// Initialize mobile audio unlock when module loads
if (typeof document !== "undefined" && isMobileBrowser()) {
  setupMobileAudioUnlock();
}

// Queue an audio element for playback when user interaction occurs
function queueAudioForPlay(audioEl) {
  if (!audioEl) return;

  if (userInteractionOccurred) {
    // User has already interacted, try to play immediately
    audioEl.play().catch((e) => {
      console.warn("🔊 [MOBILE] Queued audio play failed:", e.message);
    });
  } else {
    // Queue for later when user interacts
    if (!pendingAudioPlays.includes(audioEl)) {
      pendingAudioPlays.push(audioEl);
      console.log(
        "🔊 [MOBILE] Audio element queued for play on user interaction",
      );
    }
  }
}

// Pre-warm audio element for mobile browsers (call on user interaction)
function prewarmAudioElement(audioElement) {
  if (!audioElement) return;

  try {
    // Create a short silent audio to "unlock" the audio element on mobile
    const silentDataUri =
      "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
    audioElement.src = silentDataUri;
    audioElement.volume = 0;
    const playPromise = audioElement.play();
    if (playPromise) {
      playPromise
        .then(() => {
          audioElement.pause();
          audioElement.src = "";
          audioElement.volume = 1;
          console.log("🔊 [MOBILE] Audio element pre-warmed successfully");
        })
        .catch((e) => {
          console.warn(
            "🔊 [MOBILE] Audio prewarm failed (may work on actual call):",
            e.message,
          );
        });
    }
  } catch (e) {
    console.warn("🔊 [MOBILE] Audio prewarm error:", e.message);
  }
}

function attachRemoteAudio(session, audioElement) {
  if (!session || !audioElement) return;

  const isMobile = isMobileBrowser();
  console.log(`🔊 [AUDIO] Attaching remote audio (mobile: ${isMobile})`);

  const setStream = async (stream) => {
    if (!stream) return;
    try {
      // Ensure AudioContext is resumed (especially important for mobile)
      if (isMobile) {
        await ensureAudioContextResumed();
      }

      // Set stream to audio element
      audioElement.srcObject = stream;
      audioElement.autoplay = true;
      audioElement.muted = false;
      audioElement.volume = 1.0;

      // Mobile-specific attributes
      audioElement.setAttribute("playsinline", "");
      audioElement.setAttribute("webkit-playsinline", "");

      // Attempt to play with retry logic for mobile
      const attemptPlay = async (retries = 3) => {
        for (let i = 0; i < retries; i++) {
          try {
            await audioElement.play();
            console.log(
              `🔊 [AUDIO] ✅ Audio playback started (attempt ${i + 1})`,
            );
            return true;
          } catch (err) {
            console.warn(
              `🔊 [AUDIO] Play attempt ${i + 1} failed:`,
              err.message,
            );
            if (i < retries - 1) {
              // Wait a bit before retrying
              await new Promise((resolve) =>
                setTimeout(resolve, 100 * (i + 1)),
              );
            }
          }
        }
        return false;
      };

      const playSuccess = await attemptPlay();

      if (!playSuccess && isMobile) {
        console.warn(
          "🔊 [MOBILE] Audio autoplay blocked - user interaction may be required",
        );
        // On mobile, if autoplay fails, the audio should still work after user interaction
        // The srcObject is set, so tapping anywhere should allow audio to play
      }
    } catch (error) {
      console.warn("🔊 [AUDIO] Failed to attach remote audio stream:", error);
    }
  };

  const { connection } = session;
  if (!connection) {
    console.warn("🔊 [AUDIO] No connection object on session");
    return;
  }

  // Modern way: ontrack event
  if (typeof connection.addEventListener === "function") {
    connection.addEventListener("track", (event) => {
      console.log(
        `🔊 [AUDIO] Track event received: ${
          event.track ? event.track.kind : "unknown"
        }`,
      );
      if (event.track && event.track.kind === "audio" && event.streams) {
        const stream = event.streams[0];
        if (stream) {
          console.log(`🔊 [AUDIO] Setting stream from track event`);
          setStream(stream);
        }
      }
    });
  }

  // Fallback: getRemoteStreams (deprecated but works on some browsers)
  if (typeof connection.getRemoteStreams === "function") {
    const existing = connection.getRemoteStreams();
    if (existing && existing.length) {
      console.log(`🔊 [AUDIO] Setting stream from getRemoteStreams (fallback)`);
      setStream(existing[0]);
    }
  }

  // Additional fallback: getReceivers for modern browsers
  if (typeof connection.getReceivers === "function") {
    const receivers = connection.getReceivers();
    const audioReceiver = receivers.find(
      (r) => r.track && r.track.kind === "audio",
    );
    if (audioReceiver && audioReceiver.track) {
      const stream = new MediaStream([audioReceiver.track]);
      console.log(
        `🔊 [AUDIO] Setting stream from getReceivers (modern fallback)`,
      );
      setStream(stream);
    }
  }
}

function extractSipNumber(header) {
  if (!header) return "";
  const match = header.match(/sip:([^@;>]+)/i);
  return match ? match[1] : "";
}

function normalizeDialString(value) {
  if (!value) return "";
  const str = String(value).trim();
  const digits = str.replace(/[^0-9]/g, "");
  if (!digits) return "";
  return str.startsWith("+") ? `+${digits}` : digits;
}

function resolveSocketUrl(rootState) {
  const socketOverride = process.env.VUE_APP_SOCKET_ENDPOINT;
  if (socketOverride) return socketOverride;
  const endpoint =
    (rootState && rootState.ENDPOINT) || process.env.VUE_APP_ENDPOINT;
  if (!endpoint) return window.location.origin;
  let base = endpoint.replace(/\/meta\/api.*$/i, "").replace(/\/api\/?$/i, "");
  if (!base.startsWith("http")) {
    return window.location.origin;
  }
  return base;
}

function startCallTimer(commit) {
  stopCallTimer();
  commit("SET_CALL_DURATION", 0);
  callTimer = setInterval(() => {
    commit("INCREMENT_CALL_DURATION");
  }, 1000);
}

function stopCallTimer(commit) {
  if (callTimer) {
    clearInterval(callTimer);
    callTimer = null;
  }
  if (commit) {
    commit("SET_CALL_DURATION", 0);
  }
}

// Environment-based SIP configuration
function getSipEnvironment() {
  const hostname = window.location.hostname;
  const isDev =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("192.168.") ||
    hostname.includes("10.0.");
  return isDev ? "development" : "production";
}

function getFixedSipConfig() {
  // SIP domain is set per-environment via VUE_APP_SIP_DOMAIN in .env files
  // Staging: staging-sip.godial.cc | Production: sip.godial.cc
  const sipDomain = process.env.VUE_APP_SIP_DOMAIN || "sip.godial.cc";
  return {
    domain: sipDomain,
    wsServers: `wss://${sipDomain}/ws/`,
    iceServers: "stun:stun.l.google.com:19302",
  };
}

// Track connection attempt for cancellation
let connectionAbortController = null;

const state = {
  // Connection state
  connectionStatus: "disconnected", // disconnected, connecting, connected, registered, error
  connectionError: null, // Error message when connection fails
  connectionProgress: "", // Progress message during connection (e.g., "Connecting to WebSocket...")

  // Call state
  callStatus: "idle", // idle, calling, ringing, connected, failed
  callError: null, // Error message when call fails
  isInCall: false,
  callDuration: 0,
  currentNumber: "",
  currentContactName: "", // Contact name if selected from search
  incomingCall: null,
  incomingStatus: "idle", // idle, ringing, connecting, connected, ended
  incomingVisible: false,

  // Call popup visibility - separate from dialer dialog
  showCallPopup: false, // Show the floating call popup for active calls
  callDirection: "outgoing", // Track current call direction for popup

  // Settings - now uses userId + authToken instead of username + password
  // domain/websocket are auto-configured based on environment, but can be overridden
  settings: {
    ...getFixedSipConfig(),
    userId: "",
    authToken: "",
    displayName: "",
  },

  // Custom settings override
  useCustomSettings: false, // When true, use customSettings instead of auto-config
  customSettings: {
    domain: "",
    wsServers: "",
    iceServers: "",
  },

  // Debug logs for PBX troubleshooting
  debugLogs: [],
  maxDebugLogs: 100,

  // UI state
  isDialerOpen: false,
  showDialPad: false,
  prefillNumber: "", // Number to prefill when opening dialer (from contact popup)
  prefillContactName: "", // Contact name to prefill

  // SIP call tracking - simplified
  sipCallId: null, // SIP Call-ID from JsSIP session
  lastCallDuration: 0,
  lastCallNumber: "",
  lastCallContactName: "", // Contact name for disposition dialog
  lastCallContactId: null, // Contact ID for disposition (important for auto-dialer)
  lastCallListId: null, // List ID for disposition
  lastCallDirection: "outgoing", // outgoing or incoming
  showDispositionDialog: false,

  // Save contact dialog for incoming calls (unknown numbers)
  showIncomingSaveContactDialog: false, // Show save contact popup after incoming call ends
  pendingContactPhone: "", // Phone number to save
  pendingContactName: "", // Name entered by user
  pendingDisposition: false, // Flag to show disposition after save/skip

  // Auto-dialer state
  isAutoDialing: false, // Whether auto-dialer is currently active
  autoDialerPaused: false, // Whether auto-dialer is paused
  autoDialerContacts: [], // List of contacts to call [{id, phone, name, listId}] - now used for tracking called contacts
  autoDialerCurrentIndex: 0, // Number of contacts called so far
  autoDialerTotalContacts: 0, // Total NEW contacts in the list (for progress display)
  autoDialerListId: null, // The list being auto-dialed
  autoDialerCurrentContact: null, // Current contact being called (fetched from API)
  autoDialerCountdown: 0, // Countdown timer between calls (seconds)
  autoDialerNoMoreContacts: false, // Flag when no more contacts available
};

const getters = {
  isConnected: (state) =>
    state.connectionStatus === "connected" ||
    state.connectionStatus === "registered",

  canMakeCall: (state) =>
    state.connectionStatus === "registered" && !state.isInCall,

  connectionText: (state) => {
    // Show progress message if connecting
    if (state.connectionStatus === "connecting" && state.connectionProgress) {
      return state.connectionProgress;
    }
    const statusMap = {
      disconnected: "Offline",
      connecting: "Connecting...",
      connected: "Registering...", // WebSocket connected, waiting for SIP registration
      registered: "Ready",
      error: state.connectionError || "Connection Failed",
    };
    return statusMap[state.connectionStatus] || "Unknown";
  },

  sipEnvironment: () => getSipEnvironment(),

  sipConfig: (state) => {
    // Return custom settings if enabled, otherwise auto-config
    if (state.useCustomSettings && state.customSettings.domain) {
      return {
        domain: state.customSettings.domain || getFixedSipConfig().domain,
        wsServers:
          state.customSettings.wsServers || getFixedSipConfig().wsServers,
        iceServers:
          state.customSettings.iceServers || getFixedSipConfig().iceServers,
      };
    }
    return getFixedSipConfig();
  },

  autoSipConfig: () => getFixedSipConfig(),

  useCustomSettings: (state) => state.useCustomSettings,

  customSettings: (state) => state.customSettings,

  debugLogs: (state) => state.debugLogs,

  statusText: (state) => {
    if (state.callStatus === "failed" && state.callError) {
      // Truncate long error messages for status display
      const err = state.callError;
      return err.length > 40 ? err.substring(0, 37) + "..." : err;
    }
    const statusMap = {
      idle: "Ready",
      calling: "Calling...",
      ringing: "Ringing...",
      connected: "In Call",
      failed: "Call Failed",
    };
    return statusMap[state.callStatus] || "Unknown";
  },

  hasIncomingCall: (state) => state.incomingVisible,

  incomingCallDetails: (state) => state.incomingCall,

  incomingStatusText: (state) => {
    const statusMap = {
      idle: "Idle",
      ringing: "Incoming call",
      connecting: "Connecting...",
      connected: "In Call",
      ended: "Ended",
      declined: "Declined",
      failed: "Failed",
    };
    return statusMap[state.incomingStatus] || "Idle";
  },

  // Auto-dialer getters
  autoDialerProgress: (state) => {
    if (!state.isAutoDialing) {
      return { current: 0, total: 0, percent: 0 };
    }
    const total = state.autoDialerTotalContacts || state.autoDialerCurrentIndex;
    const current = state.autoDialerCurrentIndex;
    const percent = total > 0 ? Math.round((current / total) * 100) : 0;
    return { current, total, percent };
  },

  autoDialerStatus: (state) => {
    if (!state.isAutoDialing) return "inactive";
    if (state.autoDialerPaused) return "paused";
    if (state.isInCall) return "in-call";
    if (state.showDispositionDialog) return "disposition";
    return "ready";
  },

  // Check if PBX carrier is configured for the company
  // This is set by checkPbxCarrierConfig action during initialization
  isPbxConfigured: (state) => {
    return state.pbxCarrierConfigured;
  },
};

const mutations = {
  SET_CONNECTION_STATUS(state, status) {
    console.log(
      `[DIALER] Connection status: ${state.connectionStatus} → ${status}`,
    );
    if (state.connectionStatus === "registered" && status !== "registered") {
      console.trace("[DIALER] WARNING: Downgrade from registered state!");
    }
    state.connectionStatus = status;
    // Clear error when successfully connected/registered
    if (status === "connected" || status === "registered") {
      state.connectionError = null;
    }
    // Clear progress when not connecting
    if (status !== "connecting") {
      state.connectionProgress = "";
    }
  },

  SET_CONNECTION_ERROR(state, error) {
    state.connectionError = error;
  },

  SET_CONNECTION_PROGRESS(state, progress) {
    state.connectionProgress = progress;
  },

  SET_CALL_STATUS(state, status) {
    state.callStatus = status;
    // Clear error when status changes to something other than failed
    if (status !== "failed") {
      state.callError = null;
    }
  },

  SET_CALL_ERROR(state, error) {
    state.callError = error;
  },

  SET_IN_CALL(state, inCall) {
    state.isInCall = inCall;
  },

  SET_CALL_DURATION(state, duration) {
    state.callDuration = duration;
  },

  SET_CURRENT_NUMBER(state, number) {
    state.currentNumber = number;
  },
  SET_CURRENT_CONTACT_NAME(state, name) {
    state.currentContactName = name || "";
  },
  SET_INCOMING_CALL(state, payload) {
    const existing =
      state.incomingCall && typeof state.incomingCall === "object"
        ? state.incomingCall
        : {};
    state.incomingCall = { ...existing, ...payload };
  },
  SET_INCOMING_STATUS(state, status) {
    state.incomingStatus = status;
  },
  SET_INCOMING_VISIBLE(state, visible) {
    state.incomingVisible = visible;
  },
  CLEAR_INCOMING_CALL(state) {
    state.incomingCall = null;
    state.incomingStatus = "idle";
    state.incomingVisible = false;
  },

  // Call popup mutations
  SET_SHOW_CALL_POPUP(state, show) {
    state.showCallPopup = show;
  },
  SET_CALL_DIRECTION(state, direction) {
    state.callDirection = direction;
  },

  SET_SETTINGS(state, settings) {
    state.settings = { ...state.settings, ...settings };
  },

  SET_DIALER_OPEN(state, open) {
    state.isDialerOpen = open;
  },

  SET_DIAL_PAD_VISIBLE(state, visible) {
    state.showDialPad = visible;
  },

  SET_PREFILL_NUMBER(state, number) {
    state.prefillNumber = number;
  },

  SET_PREFILL_CONTACT_NAME(state, name) {
    state.prefillContactName = name;
  },

  CLEAR_PREFILL(state) {
    state.prefillNumber = "";
    state.prefillContactName = "";
  },

  // SIP call tracking mutations
  SET_SIP_CALL_ID(state, callId) {
    state.sipCallId = callId;
  },

  SET_LAST_CALL_DATA(
    state,
    {
      duration,
      number,
      contactName = "",
      contactId = null,
      listId = null,
      direction = "outgoing",
    },
  ) {
    state.lastCallDuration = duration;
    state.lastCallNumber = number;
    state.lastCallContactName = contactName;
    state.lastCallContactId = contactId;
    state.lastCallListId = listId;
    state.lastCallDirection = direction;
  },

  SET_SHOW_DISPOSITION_DIALOG(state, show) {
    state.showDispositionDialog = show;
  },

  CLEAR_SIP_CALL_DATA(state) {
    state.sipCallId = null;
    state.lastCallDuration = 0;
    state.lastCallNumber = "";
    state.lastCallContactName = "";
    state.lastCallContactId = null;
    state.lastCallListId = null;
    state.lastCallDirection = "outgoing";
    state.showDispositionDialog = false;
    // Also clear save contact state
    state.showIncomingSaveContactDialog = false;
    state.pendingContactPhone = "";
    state.pendingContactName = "";
    state.pendingDisposition = false;
  },

  // Save contact dialog mutations for incoming calls
  SET_SHOW_INCOMING_SAVE_CONTACT_DIALOG(state, show) {
    state.showIncomingSaveContactDialog = show;
  },

  SET_PENDING_CONTACT_DATA(state, { phone, name = "" }) {
    state.pendingContactPhone = phone || "";
    state.pendingContactName = name || "";
  },

  SET_PENDING_DISPOSITION(state, pending) {
    state.pendingDisposition = pending;
  },

  CLEAR_PENDING_CONTACT_DATA(state) {
    state.showIncomingSaveContactDialog = false;
    state.pendingContactPhone = "";
    state.pendingContactName = "";
    state.pendingDisposition = false;
  },

  // Reset call state (for clearing failed calls)
  RESET_CALL_STATE(state) {
    state.callStatus = "idle";
    state.callError = null;
    state.isInCall = false;
    state.callDuration = 0;
    state.currentNumber = "";
    state.currentContactName = "";
    state.showCallPopup = false;
  },

  INCREMENT_CALL_DURATION(state) {
    state.callDuration++;
  },
  SET_INCOMING_AUDIO_ELEMENT(state, element) {
    incomingAudioElement = element;
  },
  CLEAR_INCOMING_AUDIO(state) {
    if (incomingAudioElement) {
      try {
        incomingAudioElement.srcObject = null;
        if (typeof incomingAudioElement.pause === "function") {
          incomingAudioElement.pause();
        }
      } catch (e) {
        console.warn("Unable to clear incoming audio element", e);
      }
    }
    incomingAudioElement = null;
  },

  // Auto-dialer mutations
  SET_AUTO_DIALING(state, isAutoDialing) {
    state.isAutoDialing = isAutoDialing;
  },
  SET_AUTO_DIALER_PAUSED(state, paused) {
    state.autoDialerPaused = paused;
  },
  SET_AUTO_DIALER_CONTACTS(state, contacts) {
    state.autoDialerContacts = contacts;
  },
  SET_AUTO_DIALER_CURRENT_INDEX(state, index) {
    state.autoDialerCurrentIndex = index;
  },
  SET_AUTO_DIALER_LIST_ID(state, listId) {
    state.autoDialerListId = listId;
  },
  SET_AUTO_DIALER_CURRENT_CONTACT(state, contact) {
    state.autoDialerCurrentContact = contact;
  },
  INCREMENT_AUTO_DIALER_INDEX(state) {
    state.autoDialerCurrentIndex++;
  },
  RESET_AUTO_DIALER(state) {
    state.isAutoDialing = false;
    state.autoDialerPaused = false;
    state.autoDialerContacts = [];
    state.autoDialerCurrentIndex = 0;
    state.autoDialerTotalContacts = 0;
    state.autoDialerListId = null;
    state.autoDialerCurrentContact = null;
    state.autoDialerCountdown = 0;
    state.autoDialerNoMoreContacts = false;
  },
  SET_AUTO_DIALER_TOTAL_CONTACTS(state, total) {
    state.autoDialerTotalContacts = total;
  },
  SET_AUTO_DIALER_NO_MORE_CONTACTS(state, noMore) {
    state.autoDialerNoMoreContacts = noMore;
  },
  SET_AUTO_DIALER_COUNTDOWN(state, seconds) {
    state.autoDialerCountdown = seconds;
  },
  DECREMENT_AUTO_DIALER_COUNTDOWN(state) {
    if (state.autoDialerCountdown > 0) {
      state.autoDialerCountdown--;
    }
  },

  ADD_DEBUG_LOG(state, log) {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, ...log };
    state.debugLogs.unshift(logEntry);
    // Keep only last maxDebugLogs entries
    if (state.debugLogs.length > state.maxDebugLogs) {
      state.debugLogs = state.debugLogs.slice(0, state.maxDebugLogs);
    }
  },

  CLEAR_DEBUG_LOGS(state) {
    state.debugLogs = [];
  },

  SET_USE_CUSTOM_SETTINGS(state, value) {
    state.useCustomSettings = value;
  },

  SET_CUSTOM_SETTINGS(state, settings) {
    state.customSettings = { ...state.customSettings, ...settings };
  },
};

const actions = {
  // Initialize dialer state - MUST be called on app startup
  // This fixes the state desync issue where Vuex state is persisted but userAgentInstance is lost on refresh
  async initializeDialer({ commit, state, dispatch, rootState }) {
    console.log("[Dialer] Initializing... Current state:", {
      connectionStatus: state.connectionStatus,
      hasUserAgent: !!userAgentInstance,
    });

    // If state says we're connected/registered but we don't have a UserAgent instance,
    // reset the connection state to disconnected
    if (state.connectionStatus !== "disconnected" && !userAgentInstance) {
      console.log(
        "[Dialer] State desync detected! Resetting connection status to disconnected.",
      );
      commit("SET_CONNECTION_STATUS", "disconnected");
      commit("SET_CALL_STATUS", "idle");
      commit("SET_IN_CALL", false);
    }

    // Load settings first
    dispatch("loadSettings");

    // Auto-connect if we have credentials
    const user = rootState && rootState.user;
    const userId = user && (user.id || user.userId || user.username);
    const authToken = user && user.token;

    if (userId && authToken && state.connectionStatus === "disconnected") {
      console.log("[Dialer] Auto-connecting with user credentials...");
      try {
        await dispatch("connect");
        console.log("[Dialer] Auto-connect successful!");
      } catch (error) {
        console.warn("[Dialer] Auto-connect failed:", error.message);
      }
    }
  },

  addDebugLog({ commit }, log) {
    commit("ADD_DEBUG_LOG", log);
  },

  clearDebugLogs({ commit }) {
    commit("CLEAR_DEBUG_LOGS");
  },

  /**
   * Update agent SIP status on the server
   * Called when agent becomes busy (in call) or available (call ended)
   * This allows the server to route incoming calls only to available agents
   * @param {string} status - 'busy' | 'available' | 'connected' | 'disconnected'
   */
  async updateAgentSipStatus({ rootState, dispatch }, status) {
    try {
      const endpoint = rootState.ENDPOINT || process.env.VUE_APP_ENDPOINT;
      const user = rootState.user;
      const companyDetails = rootState.companyDetails || {};

      if (!user || !user.id) {
        console.warn("[SIP_STATUS] No user logged in, skipping status update");
        return;
      }

      const payload = {
        agentId: user.id,
        username: user.username,
        status: status,
        companyId: companyDetails.id,
      };

      console.log(
        `📡 [SIP_STATUS] Updating agent status to: ${status}`,
        payload,
      );

      // Use Vue.http which has auth token configured
      await Vue.http.post(`${endpoint}/pbx/agent-status`, payload);

      console.log(`✅ [SIP_STATUS] Agent status updated to: ${status}`);

      dispatch("addDebugLog", {
        level: "info",
        message: `Agent SIP status updated: ${status}`,
        details: { agentId: user.id, status },
      });
    } catch (err) {
      // Non-fatal - don't block call flow for status update failures
      console.warn(
        `⚠️ [SIP_STATUS] Failed to update agent status: ${err.message}`,
      );
      dispatch("addDebugLog", {
        level: "warn",
        message: `Failed to update agent status: ${err.message}`,
      });
    }
  },

  /**
   * Check if a contact exists for the given phone number.
   * If not found, show the save contact dialog before disposition.
   * If found, show disposition dialog directly.
   */
  async checkAndShowSaveContactOrDisposition(
    { commit, state, rootState, dispatch },
    { phone, direction = "incoming" },
  ) {
    console.log(
      "🔍 [SAVE_CONTACT_CHECK] Starting check for phone:",
      phone,
      "direction:",
      direction,
    );

    // Check if caller is anonymous/unavailable - skip save contact dialog
    const incomingCallData = state.incomingCall || {};
    const isAnonymous =
      incomingCallData.isAnonymous ||
      !phone ||
      phone.toLowerCase() === "anonymous" ||
      phone.toLowerCase() === "unavailable" ||
      phone.toLowerCase() === "restricted" ||
      phone.toLowerCase() === "private";

    if (isAnonymous) {
      console.log("📋 Anonymous/Private caller, showing disposition directly");
      // For anonymous callers, use the DID if available for record keeping
      const displayNumber = incomingCallData.did || phone || "Anonymous";
      commit("SET_LAST_CALL_DATA", {
        duration: state.lastCallDuration,
        number: displayNumber,
        contactName: "Anonymous Caller",
        direction: direction,
        isAnonymous: true,
      });
      commit("SET_SHOW_DISPOSITION_DIALOG", true);
      return;
    }

    // Check if we already looked up this contact during the call (incoming call lookup)
    // Note: incomingCallData already declared above
    if (
      incomingCallData.contactFound === true &&
      incomingCallData.contactName
    ) {
      console.log(
        "✅ [SAVE_CONTACT_CHECK] Using cached contact from incoming call:",
        incomingCallData.contactName,
      );
      commit("SET_LAST_CALL_DATA", {
        duration: state.lastCallDuration,
        number: state.lastCallNumber,
        contactName: incomingCallData.contactName || "",
        direction: direction,
      });
      commit("SET_SHOW_DISPOSITION_DIALOG", true);
      return;
    }

    if (incomingCallData.contactFound === false) {
      console.log(
        "🆕 [SAVE_CONTACT_CHECK] Contact was not found during incoming call, showing save dialog",
      );
      commit("SET_PENDING_CONTACT_DATA", { phone, name: "" });
      commit("SET_PENDING_DISPOSITION", true);
      commit("SET_SHOW_INCOMING_SAVE_CONTACT_DIALOG", true);
      return;
    }

    // If contactFound is undefined, we need to do the lookup (fallback)
    console.log(
      "🔍 [SAVE_CONTACT_CHECK] No cached lookup result, doing fresh lookup",
    );

    try {
      // Try using the lookupContactByPhone action
      const contact = await dispatch("lookupContactByPhone", phone);

      if (contact) {
        console.log(
          "✅ [SAVE_CONTACT_CHECK] Contact found via lookup:",
          contact.name,
        );
        commit("SET_LAST_CALL_DATA", {
          duration: state.lastCallDuration,
          number: state.lastCallNumber,
          contactName: contact.name || "",
          direction: direction,
        });
        commit("SET_SHOW_DISPOSITION_DIALOG", true);
      } else {
        console.log(
          "🆕 [SAVE_CONTACT_CHECK] Contact NOT found, showing save contact dialog",
        );
        commit("SET_PENDING_CONTACT_DATA", { phone, name: "" });
        commit("SET_PENDING_DISPOSITION", true);
        commit("SET_SHOW_INCOMING_SAVE_CONTACT_DIALOG", true);
      }
    } catch (err) {
      console.error("❌ Error checking contact:", err);
      // On error, show save contact dialog instead of going directly to disposition
      console.log(
        "🆕 Error occurred, showing save contact dialog for user input",
      );
      commit("SET_PENDING_CONTACT_DATA", { phone, name: "" });
      commit("SET_PENDING_DISPOSITION", true);
      commit("SET_SHOW_INCOMING_SAVE_CONTACT_DIALOG", true);
    }
  },

  /**
   * Look up a contact by phone number and update call data with contact name.
   * Called during incoming call ringing phase.
   * Returns the contact if found, null otherwise.
   */
  async lookupContactByPhone({ commit, state, rootState }, phone) {
    console.log("🔍 [CONTACT_LOOKUP] Looking up contact for phone:", phone);

    if (!phone) {
      console.log("⚠️ [CONTACT_LOOKUP] No phone number provided");
      return null;
    }

    // Skip lookup for anonymous/unavailable callers
    const normalizedPhone = phone.replace(/[^\d]/g, "");
    const isAnonymous =
      !normalizedPhone ||
      phone.toLowerCase() === "anonymous" ||
      phone.toLowerCase() === "unavailable" ||
      phone.toLowerCase() === "restricted" ||
      phone.toLowerCase() === "private";

    if (isAnonymous) {
      console.log(
        "⚠️ [CONTACT_LOOKUP] Anonymous/Private caller - skipping lookup",
      );
      // Mark as anonymous so we skip save contact dialog too
      if (state.incomingCall) {
        commit("SET_INCOMING_CALL", {
          ...state.incomingCall,
          isAnonymous: true,
          contactFound: false,
        });
      }
      return null;
    }

    try {
      const companyDetails = rootState.companyDetails || {};
      const companyId = companyDetails.id;
      const teams = rootState.teams || [];

      if (!companyId) {
        console.warn("⚠️ [CONTACT_LOOKUP] No company ID");
        return null;
      }

      // Get team IDs (exclude 'allTeams')
      const teamIds = teams
        .filter((t) => t && t.id !== "allTeams")
        .map((t) => t.id);

      if (teamIds.length === 0) {
        console.warn("⚠️ [CONTACT_LOOKUP] No teams found");
        return null;
      }

      const endpoint = rootState.ENDPOINT || process.env.VUE_APP_ENDPOINT;

      // Fetch list IDs for these teams (Vue.http has auth configured)
      const listsResponse = await Vue.http.post(`${endpoint}/lists/fetch`, {
        teams: teamIds,
      });

      const lists = listsResponse.body || listsResponse.data || [];
      const listIds = lists.map((list) => list.id);

      if (listIds.length === 0) {
        console.warn("⚠️ [CONTACT_LOOKUP] No lists found");
        return null;
      }

      // Search for contact by phone (try both with and without country code variants)
      const searchPhone = phone.replace(/[^\d]/g, ""); // Strip non-digits
      console.log("🔍 [CONTACT_LOOKUP] Searching for phone:", searchPhone);

      const response = await Vue.http.post(
        `${endpoint}/contacts/query-contact-multilist-v2`,
        {
          search: searchPhone,
          listIds: listIds,
        },
      );

      const contacts = response.body || response.data || [];
      console.log("🔍 [CONTACT_LOOKUP] Search results:", contacts.length);

      if (contacts.length > 0) {
        const contact = contacts[0];
        console.log(
          "✅ [CONTACT_LOOKUP] Contact found:",
          contact.name,
          "id:",
          contact.id,
        );

        // Update incoming call data with contact name
        if (state.incomingCall) {
          commit("SET_INCOMING_CALL", {
            ...state.incomingCall,
            contactName: contact.name,
            contactId: contact.id,
            contactFound: true,
          });
        }

        return contact;
      } else {
        console.log("🆕 [CONTACT_LOOKUP] Contact NOT found for:", searchPhone);

        // Mark that we checked but didn't find a contact
        if (state.incomingCall) {
          commit("SET_INCOMING_CALL", {
            ...state.incomingCall,
            contactFound: false,
          });
        }

        return null;
      }
    } catch (err) {
      console.error("❌ [CONTACT_LOOKUP] Error:", err);
      return null;
    }
  },

  loadSettings({ commit, state, rootState }) {
    // Load custom settings from localStorage if any
    const savedCustomSettings = localStorage.getItem(
      "godial-dialer-custom-settings",
    );
    if (savedCustomSettings) {
      try {
        const parsed = JSON.parse(savedCustomSettings);
        if (parsed.useCustomSettings) {
          commit("SET_USE_CUSTOM_SETTINGS", true);
          commit("SET_CUSTOM_SETTINGS", parsed.customSettings || {});
        }
      } catch (e) {
        console.warn("[Dialer] Failed to load custom settings:", e);
      }
    }

    // Get effective SIP config (custom or auto)
    const effectiveConfig =
      state.useCustomSettings && state.customSettings.domain
        ? {
            domain: state.customSettings.domain,
            wsServers:
              state.customSettings.wsServers || getFixedSipConfig().wsServers,
            iceServers:
              state.customSettings.iceServers || getFixedSipConfig().iceServers,
          }
        : getFixedSipConfig();

    // Get user credentials from root state
    const user = rootState && rootState.user;
    const userId = user && (user.id || user.userId || user.username);
    const authToken = user && user.token;
    const displayName = user && (user.name || user.username || "");

    const settings = {
      ...effectiveConfig,
      userId: userId || "",
      authToken: authToken || "",
      displayName: displayName,
    };

    commit("SET_SETTINGS", settings);

    console.log("[Dialer] Settings loaded:", {
      environment: getSipEnvironment(),
      useCustomSettings: state.useCustomSettings,
      domain: settings.domain,
      wsServers: settings.wsServers,
      userId: settings.userId,
      hasAuthToken: !!settings.authToken,
    });
  },

  // Save custom SIP settings override
  saveCustomSettings(
    { commit, state, dispatch },
    { domain, wsServers, iceServers },
  ) {
    // Disconnect existing connection since settings are changing
    dispatch("disconnect");

    commit("SET_USE_CUSTOM_SETTINGS", true);
    commit("SET_CUSTOM_SETTINGS", { domain, wsServers, iceServers });

    // Persist to localStorage
    localStorage.setItem(
      "godial-dialer-custom-settings",
      JSON.stringify({
        useCustomSettings: true,
        customSettings: { domain, wsServers, iceServers },
      }),
    );

    // Update main settings with new config
    const effectiveConfig = {
      domain: domain || getFixedSipConfig().domain,
      wsServers: wsServers || getFixedSipConfig().wsServers,
      iceServers: iceServers || getFixedSipConfig().iceServers,
    };

    commit("SET_SETTINGS", {
      ...state.settings,
      ...effectiveConfig,
    });

    console.log(
      "[Dialer] Custom settings saved (disconnected for new config):",
      {
        domain,
        wsServers,
        iceServers,
      },
    );
  },

  // Reset to auto-configured settings
  resetToAutoSettings({ commit, dispatch }) {
    // Disconnect existing connection since settings are changing
    dispatch("disconnect");

    commit("SET_USE_CUSTOM_SETTINGS", false);
    commit("SET_CUSTOM_SETTINGS", {
      domain: "",
      wsServers: "",
      iceServers: "",
    });
    localStorage.removeItem("godial-dialer-custom-settings");
    dispatch("loadSettings");
  },

  // Legacy saveSettings - now just updates display name
  saveSettings({ commit, state }, newSettings) {
    const mergedSettings = {
      ...state.settings,
      displayName: newSettings.displayName || state.settings.displayName,
    };
    commit("SET_SETTINGS", mergedSettings);
  },

  setIncomingAudioElement({ commit }, element) {
    commit("SET_INCOMING_AUDIO_ELEMENT", element);
    if (!element) return;
    if (incomingSessionInstance) {
      attachRemoteAudio(incomingSessionInstance, element);
    } else if (sessionInstance) {
      attachRemoteAudio(sessionInstance, element);
    }
  },

  async answerIncomingCall({ commit, state }, { audioElement } = {}) {
    // Check if we have a session, if not wait a bit for it to arrive
    if (!incomingSessionInstance) {
      console.log("[ANSWER] ⏳ No session yet, waiting for JsSIP INVITE...");

      // Wait up to 10 seconds for the session to arrive (increased from 3s)
      // This gives more time for the SIP INVITE to traverse the network
      const maxWait = 10000;
      const checkInterval = 100;
      let waited = 0;

      while (!incomingSessionInstance && waited < maxWait) {
        await new Promise((resolve) => setTimeout(resolve, checkInterval));
        waited += checkInterval;
        if (waited % 1000 === 0) {
          console.log(
            `[ANSWER] ⏳ Still waiting for session... (${waited / 1000}s)`,
          );
        }
      }

      if (!incomingSessionInstance) {
        console.error(
          "[ANSWER] ❌ Timeout waiting for SIP session after",
          maxWait / 1000,
          "seconds",
        );
        console.error(
          "[ANSWER] 💡 This usually means the SIP INVITE never reached JsSIP",
        );
        console.error(
          "[ANSWER] 💡 Check: WebSocket connection, Kamailio routing, RTPEngine",
        );
        throw new Error(
          "SIP session not ready. The call may have been cancelled or routing failed.",
        );
      }

      console.log("[ANSWER] ✅ Session ready after", waited, "ms");
    }

    if (audioElement) {
      commit("SET_INCOMING_AUDIO_ELEMENT", audioElement);
    }

    const targetNumber =
      (state.incomingCall && state.incomingCall.from) ||
      (incomingSessionInstance &&
        incomingSessionInstance.remote_identity &&
        normalizeDialString(
          extractSipNumber(
            incomingSessionInstance.remote_identity.uri
              ? incomingSessionInstance.remote_identity.uri.toString()
              : "",
          ),
        )) ||
      "";

    commit("SET_CURRENT_NUMBER", targetNumber);
    commit("SET_INCOMING_STATUS", "connecting");
    commit("SET_CALL_STATUS", "ringing");
    commit("SET_INCOMING_VISIBLE", true);

    // Capture SIP Call-ID from incoming session
    if (incomingSessionInstance && incomingSessionInstance.id) {
      const originalCallId = incomingSessionInstance.id;
      const sipCallId = originalCallId.substring(0, 20);

      console.log(
        "🔍 [INCOMING] JsSIP Original Call-ID:",
        originalCallId,
        `(${originalCallId.length} chars)`,
      );
      console.log(
        "✂️  [INCOMING] Truncated Call-ID:",
        sipCallId,
        "(20 chars for RTPEngine)",
      );

      commit("SET_SIP_CALL_ID", sipCallId);
      console.log("📞 [INCOMING] SIP Call-ID stored:", sipCallId);
    }

    const mediaRequestConstraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    };

    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === "function"
      ) {
        await navigator.mediaDevices.getUserMedia(mediaRequestConstraints);
      }
    } catch (error) {
      console.error("❌ Microphone permission denied for incoming call", error);
      commit("SET_INCOMING_STATUS", "failed");
      commit("SET_CALL_STATUS", "failed");
      commit("SET_INCOMING_VISIBLE", false);
      try {
        incomingSessionInstance.terminate({
          status_code: 480,
          reason_phrase: "Media unavailable",
        });
      } catch (terminateError) {
        console.warn(
          "⚠️ Failed to terminate session after media error",
          terminateError,
        );
      }
      commit("CLEAR_INCOMING_AUDIO");
      setTimeout(() => {
        commit("CLEAR_INCOMING_CALL");
      }, 500);
      incomingSessionInstance = null;
      sessionInstance = null;
      throw error;
    }

    const iceServerList = (state.settings.iceServers || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((url) => ({ urls: url }));

    const answerOptions = {
      mediaConstraints: mediaRequestConstraints,
      rtcOfferConstraints: {
        mandatory: {
          OfferToReceiveAudio: true,
          OfferToReceiveVideo: false,
        },
      },
    };

    if (iceServerList.length) {
      answerOptions.pcConfig = { iceServers: iceServerList };
    }

    try {
      console.log(
        "[ANSWER] 📞 Calling session.answer() with options:",
        answerOptions,
      );

      // Pre-warm audio on mobile (answering is a user interaction)
      if (isMobileBrowser()) {
        console.log("[ANSWER] 📱 Mobile browser detected - preparing audio");
        await ensureAudioContextResumed();
        if (incomingAudioElement) {
          prewarmAudioElement(incomingAudioElement);
        }
      }

      // Set up peerconnection event for audio before answering
      incomingSessionInstance.on("peerconnection", (data) => {
        console.log("[ANSWER] 🔌 PeerConnection created");
        const pc = data.peerconnection;

        pc.ontrack = async (event) => {
          console.log(
            "[ANSWER] 🔊 Got remote track:",
            event.track.kind,
            "readyState:",
            event.track.readyState,
          );
          if (event.track.kind === "audio" && incomingAudioElement) {
            console.log("[ANSWER] 🔊 Attaching audio track to element");
            if (event.streams && event.streams[0]) {
              // Ensure AudioContext is resumed on mobile
              if (isMobileBrowser()) {
                await ensureAudioContextResumed();
              }

              incomingAudioElement.srcObject = event.streams[0];
              incomingAudioElement.volume = 1.0;
              incomingAudioElement.muted = false;

              // Mobile-specific attributes
              incomingAudioElement.setAttribute("playsinline", "");
              incomingAudioElement.setAttribute("webkit-playsinline", "");

              // Retry play with exponential backoff for mobile
              const attemptPlay = async (maxRetries = 3) => {
                for (let i = 0; i < maxRetries; i++) {
                  try {
                    await incomingAudioElement.play();
                    console.log(
                      `[ANSWER] 🔊 ✅ Audio playing (attempt ${i + 1})`,
                    );
                    return;
                  } catch (e) {
                    console.warn(
                      `[ANSWER] 🔊 Play attempt ${i + 1} failed:`,
                      e.message,
                    );
                    if (i < maxRetries - 1) {
                      await new Promise((r) => setTimeout(r, 100 * (i + 1)));
                    }
                  }
                }
                console.warn(
                  "[ANSWER] 🔊 All play attempts failed - audio may require user tap",
                );
              };

              attemptPlay();
            }
          }
        };

        pc.oniceconnectionstatechange = () => {
          console.log("[ANSWER] 🧊 ICE state:", pc.iceConnectionState);
        };

        // Check senders for microphone status
        setTimeout(() => {
          const senders = pc.getSenders();
          senders.forEach((sender, i) => {
            if (sender.track) {
              console.log(
                `[ANSWER] 📤 Sender ${i}: ${sender.track.kind} track, enabled: ${sender.track.enabled}, muted: ${sender.track.muted}`,
              );
            }
          });
          const receivers = pc.getReceivers();
          receivers.forEach((receiver, i) => {
            if (receiver.track) {
              console.log(
                `[ANSWER] 📥 Receiver ${i}: ${receiver.track.kind} track, enabled: ${receiver.track.enabled}`,
              );
            }
          });
        }, 2000);
      });

      incomingSessionInstance.answer(answerOptions);
      console.log("[ANSWER] ✅ answer() called successfully");

      sessionInstance = incomingSessionInstance;

      // Store SIP Call-ID for incoming call (needed for disposition)
      const sipCallId =
        incomingSessionInstance._request &&
        incomingSessionInstance._request.call_id;
      if (sipCallId) {
        commit("SET_SIP_CALL_ID", sipCallId);
        console.log("[ANSWER] 📞 SIP Call-ID stored:", sipCallId);
      }

      // Set current number from incoming call data or session
      const incomingFrom =
        (state.incomingCall && state.incomingCall.from) || targetNumber;
      if (incomingFrom) {
        commit("SET_CURRENT_NUMBER", incomingFrom);
        console.log("[ANSWER] 📞 Current number set:", incomingFrom);
      }

      // Set in-call state immediately since we're answering
      // Don't wait for 'confirmed' event - it may not fire reliably
      commit("SET_IN_CALL", true);
      commit("SET_INCOMING_STATUS", "connected");
      commit("SET_CALL_STATUS", "connected");
      // CRITICAL: Keep popup visible during connected call
      commit("SET_INCOMING_VISIBLE", true);
      startCallTimer(commit);

      console.log(
        "[ANSWER] 📞 Call status set to CONNECTED, popup should be visible",
      );

      // Robust remote audio setup function
      const setupIncomingAudio = async () => {
        if (!incomingSessionInstance || !incomingSessionInstance.connection) {
          console.warn("[ANSWER] 🔊 No session connection for audio setup");
          return;
        }

        const pc = incomingSessionInstance.connection;

        // Get audio element
        let audioEl =
          incomingAudioElement ||
          document.getElementById("incoming-call-audio");
        if (!audioEl) {
          console.log("[ANSWER] 🔊 Creating fallback audio element");
          audioEl = document.createElement("audio");
          audioEl.id = "incoming-audio-fallback";
          audioEl.autoplay = true;
          audioEl.setAttribute("playsinline", "");
          audioEl.setAttribute("webkit-playsinline", "");
          document.body.appendChild(audioEl);
          incomingAudioElement = audioEl;
        }

        // Resume AudioContext on mobile
        if (isMobileBrowser()) {
          await ensureAudioContextResumed();
        }

        let streamAttached = false;

        // Method 1: getReceivers (modern)
        if (!streamAttached && typeof pc.getReceivers === "function") {
          const receivers = pc.getReceivers();
          console.log("[ANSWER] 📥 Checking receivers:", receivers.length);
          const audioReceiver = receivers.find(
            (r) => r.track && r.track.kind === "audio",
          );
          if (audioReceiver && audioReceiver.track) {
            console.log(
              "[ANSWER] 📥 Found audio receiver, readyState:",
              audioReceiver.track.readyState,
            );
            const stream = new MediaStream([audioReceiver.track]);
            audioEl.srcObject = stream;
            streamAttached = true;
            console.log("[ANSWER] 🔊 Audio attached via getReceivers");
          }
        }

        // Method 2: getRemoteStreams
        if (!streamAttached && typeof pc.getRemoteStreams === "function") {
          const remoteStreams = pc.getRemoteStreams();
          console.log(
            "[ANSWER] 🔊 Remote streams count:",
            remoteStreams.length,
          );
          if (remoteStreams.length > 0) {
            audioEl.srcObject = remoteStreams[0];
            streamAttached = true;
            console.log("[ANSWER] 🔊 Audio attached via getRemoteStreams");
          }
        }

        // Method 3: Already set by ontrack
        if (!streamAttached && audioEl.srcObject) {
          console.log("[ANSWER] 🔊 srcObject already set (by ontrack)");
          streamAttached = true;
        }

        if (streamAttached) {
          audioEl.volume = 1.0;
          audioEl.muted = false;

          for (let attempt = 1; attempt <= 5; attempt++) {
            try {
              await audioEl.play();
              console.log(`[ANSWER] 🔊 ✅ Audio playing on attempt ${attempt}`);

              setTimeout(() => {
                console.log("[ANSWER] 🔊 Audio state:", {
                  paused: audioEl.paused,
                  muted: audioEl.muted,
                  volume: audioEl.volume,
                  readyState: audioEl.readyState,
                });
              }, 1000);

              return;
            } catch (e) {
              console.warn(
                `[ANSWER] 🔊 Play attempt ${attempt} failed:`,
                e.message,
              );
              await new Promise((r) => setTimeout(r, 200 * attempt));
            }
          }
          console.warn(
            "[ANSWER] 🔊 ⚠️ All play attempts failed - queuing for user interaction",
          );
          queueAudioForPlay(audioEl);
        } else {
          console.warn("[ANSWER] 🔊 ⚠️ No remote stream found!");
        }
      };

      // Run audio setup immediately and with delays
      setupIncomingAudio();
      setTimeout(setupIncomingAudio, 500);
      setTimeout(setupIncomingAudio, 1500);
      setTimeout(setupIncomingAudio, 3000);

      if (incomingAudioElement) {
        attachRemoteAudio(incomingSessionInstance, incomingAudioElement);
      }
      return incomingSessionInstance;
    } catch (error) {
      console.error("❌ Failed to answer incoming call", error);
      commit("SET_INCOMING_STATUS", "failed");
      commit("SET_CALL_STATUS", "failed");
      commit("SET_INCOMING_VISIBLE", false);
      commit("CLEAR_INCOMING_AUDIO");
      setTimeout(() => {
        commit("CLEAR_INCOMING_CALL");
      }, 500);
      sessionInstance = null;
      incomingSessionInstance = null;
      throw error;
    }
  },

  rejectIncomingCall({ commit }) {
    commit("SET_INCOMING_STATUS", "declined");
    commit("SET_INCOMING_VISIBLE", false);
    commit("SET_IN_CALL", false);
    commit("SET_CALL_STATUS", "idle");
    commit("SET_CURRENT_NUMBER", "");
    stopCallTimer(commit);

    const session = incomingSessionInstance;
    if (session) {
      try {
        session.terminate({
          status_code: 486,
          reason_phrase: "Busy Here",
        });
      } catch (error) {
        console.warn("⚠️ Error rejecting incoming call:", error);
      }
    }

    // Always clear the call state
    commit("CLEAR_INCOMING_AUDIO");
    setTimeout(() => {
      commit("CLEAR_INCOMING_CALL");
    }, 100);

    if (sessionInstance === session) {
      sessionInstance = null;
    }

    incomingSessionInstance = null;
  },

  // Cancel any ongoing connection attempt
  cancelConnection({ commit, dispatch }) {
    console.log("[DIALER] Cancelling connection attempt...");

    // Signal cancellation
    if (connectionAbortController) {
      connectionAbortController.abort();
      connectionAbortController = null;
    }

    // Stop and cleanup UserAgent
    if (userAgentInstance) {
      try {
        userAgentInstance.stop();
      } catch (e) {
        console.warn("[DIALER] Error stopping UA during cancel:", e);
      }
      userAgentInstance = null;
    }

    commit("SET_CONNECTION_STATUS", "disconnected");
    commit("SET_CONNECTION_ERROR", null);
    commit("SET_CONNECTION_PROGRESS", "");

    dispatch("addDebugLog", {
      level: "warning",
      message: "Connection cancelled by user",
    });
  },

  async connect({ commit, state, dispatch, rootState, rootGetters }) {
    // Cancel any previous connection attempt
    if (connectionAbortController) {
      connectionAbortController.abort();
    }
    connectionAbortController = new AbortController();
    const abortSignal = connectionAbortController;

    if (userAgentInstance) {
      userAgentInstance.stop();
      userAgentInstance = null;
    }

    // Clear any previous errors
    commit("SET_CONNECTION_ERROR", null);
    commit("SET_CONNECTION_PROGRESS", "Initializing...");

    // Reload settings to ensure we have latest user credentials
    dispatch("loadSettings");

    const {
      domain,
      userId,
      authToken,
      wsServers,
      displayName,
      iceServers,
    } = state.settings;

    // Use userId for SIP registration (NOT username/email which may contain @)
    // This is CRITICAL because email addresses like "user@gmail.com" create invalid SIP URIs:
    // "sip:user@gmail.com@sip.godial.cc" has two @ symbols and fails
    // The SIP URI must use userId which is a safe alphanumeric string
    const user = rootState && rootState.user;
    const sipUsername = user && (user.id || userId);
    const password = authToken;

    if (!sipUsername || !password || !domain) {
      const errorMsg =
        "Missing required settings: Please login to use the dialer";
      commit("SET_CONNECTION_ERROR", errorMsg);
      commit("SET_CONNECTION_STATUS", "error");
      dispatch("addDebugLog", {
        level: "error",
        message: errorMsg,
        details: { hasUserId: !!sipUsername, hasToken: !!authToken, domain },
      });
      throw new Error(errorMsg);
    }

    dispatch("addDebugLog", {
      level: "info",
      message: "Connecting to SIP server",
      details: { domain, wsServers, userId: sipUsername },
    });
    commit("SET_CONNECTION_STATUS", "connecting");
    commit("SET_CONNECTION_PROGRESS", "Connecting to " + domain + "...");

    if (!socketInstance) {
      const url = resolveSocketUrl(rootState);
      socketInstance = io(url, {
        transports: ["websocket"],
        withCredentials: true,
      });

      socketInstance.on("connect", () => {
        console.log("🔌 Socket connected");
        const user = (rootState && rootState.user) || {};
        if (user.companyId) {
          console.log(
            "[Socket.io] 📞 Joining company room for PBX events:",
            user.companyId,
          );
          // Include agentId to also join agent-specific room
          socketInstance.emit("join-company", {
            companyId: user.companyId,
            agentId: user.id,
          });
          joinedCompanyId = user.companyId;
        } else {
          console.warn(
            "[Socket.io] ⚠️  No companyId found, cannot join PBX room",
          );
        }
      });

      socketInstance.on("disconnect", () => {
        console.log("🔌 Socket disconnected");
        joinedCompanyId = null;
      });

      socketInstance.on("pbx:incoming-call", (payload) => {
        const incomingCallId =
          (payload && payload.id) || (payload && payload.callId);

        console.log("[PBX UI] 📞 Socket event received - INCOMING CALL:", {
          callId: incomingCallId,
          from: payload && payload.from,
          callerName: payload && payload.callerName,
          did: payload && payload.did,
          strategy: payload && payload.strategy,
          agent: payload && payload.agent,
          candidateCount:
            payload && payload.candidates && payload.candidates.length,
          timestamp: new Date().toISOString(),
        });

        // ============ PBX PLAN/SUBSCRIPTION CHECK ============
        // Block incoming calls if user doesn't have PBX plan or subscription expired
        const isPbxLocked = rootGetters.isPbxLocked;
        if (isPbxLocked) {
          console.log(
            "🚫 [PBX UI] *** PBX LOCKED - Blocking incoming call (no PBX plan or subscription expired) ***",
          );
          return;
        }
        // ============ END PBX PLAN CHECK ============

        // ============ BUSY CHECK - Ignore incoming call socket event if already in a call ============
        // This prevents the connected call UI from being corrupted by incoming call state
        const isBusy =
          state.isInCall ||
          state.callStatus === "connected" ||
          sessionInstance ||
          incomingSessionInstance;

        if (isBusy) {
          console.log(
            "📵 [PBX UI] *** BUSY - Ignoring socket incoming call event ***",
          );
          console.log("📵 [PBX UI] Current state:", {
            isInCall: state.isInCall,
            callStatus: state.callStatus,
            hasSession: !!sessionInstance,
            hasIncomingSession: !!incomingSessionInstance,
          });
          // Don't show incoming call UI - let the JsSIP handler reject the actual SIP session
          return;
        }
        // ============ END BUSY CHECK ============

        // Process socket event to show incoming call popup
        // This works even if JsSIP hasn't received the SIP INVITE yet
        if (!payload) {
          console.warn("[PBX UI] ⚠️  Empty payload, ignoring");
          return;
        }

        // Check if this incoming call is meant for the current user
        // For specific agent routing, only the target agent should see the popup
        const currentUser = (rootState && rootState.user) || {};
        const currentUserId = currentUser.id;
        const currentUsername = currentUser.username;

        if (
          payload.strategy === "agent" ||
          payload.strategy === "random-agent"
        ) {
          // For specific agent or random-agent routing, only the primary
          // selected agent should see the popup (server picks one agent)
          const targetAgent = payload.agent;
          const isTargetAgent =
            targetAgent &&
            (String(targetAgent.id) === String(currentUserId) ||
              targetAgent.username === currentUsername ||
              targetAgent.sipUsername === currentUsername);

          if (!isTargetAgent) {
            console.log(
              "[PBX UI] ⚠️  Call not for this user (strategy: " +
                payload.strategy +
                "), ignoring:",
              {
                targetAgentId: targetAgent && targetAgent.id,
                targetUsername: targetAgent && targetAgent.username,
                currentUserId,
                currentUsername,
              },
            );
            return;
          }
        } else if (
          payload.strategy === "ring-all" &&
          payload.candidates &&
          Array.isArray(payload.candidates)
        ) {
          // For ring-all, check if current user is in candidates list
          const isCandidate = payload.candidates.some(
            (candidate) =>
              candidate &&
              (String(candidate.id) === String(currentUserId) ||
                candidate.username === currentUsername ||
                candidate.sipUsername === currentUsername),
          );

          if (!isCandidate) {
            console.log(
              "[PBX UI] ⚠️  User not in ring-all candidates, ignoring:",
              {
                candidateIds: payload.candidates.map((c) => c && c.id),
                currentUserId,
              },
            );
            return;
          }
        }

        // Build call data from socket payload
        // Detect anonymous/unavailable callers
        const fromNumber = payload.from || "";
        const callerName = payload.callerName || payload.name || "";
        const isAnonymous =
          !fromNumber ||
          fromNumber.toLowerCase() === "anonymous" ||
          fromNumber.toLowerCase() === "unavailable" ||
          fromNumber.toLowerCase() === "restricted" ||
          fromNumber.toLowerCase() === "private" ||
          callerName.toLowerCase() === "unavailable" ||
          callerName.toLowerCase() === "anonymous";

        // For anonymous callers, show the DID (the number they called) instead
        const displayFrom =
          isAnonymous && payload.did ? `Call to ${payload.did}` : fromNumber;
        const displayName = isAnonymous
          ? payload.did
            ? `Private (via ${payload.did})`
            : "Private Caller"
          : callerName || fromNumber || "";

        const callData = {
          callId: incomingCallId,
          from: fromNumber, // Keep original for logging
          callerName: callerName,
          displayName: displayName, // Show DID for anonymous
          displayNumber: displayFrom, // For UI display
          did: payload.did || "",
          strategy: payload.strategy || "",
          direction: "incoming",
          timestamp: new Date().toISOString(),
          isAnonymous: isAnonymous,
        };

        console.log(
          "[PBX UI] 💾 Setting incoming call state from socket:",
          callData,
        );
        commit("SET_INCOMING_CALL", callData);

        console.log("[PBX UI] 🎯 Setting UI state to RINGING");
        commit("SET_INCOMING_STATUS", "ringing");
        commit("SET_CALL_STATUS", "ringing");

        console.log("[PBX UI] 🔔 SHOWING INCOMING CALL POPUP NOW!");
        commit("SET_INCOMING_VISIBLE", true);
      });

      socketInstance.on("pbx:incoming-call-status", (event) => {
        console.log("[PBX UI] 📊 Received call status update:", {
          callId: event && event.callId,
          status: event && event.status,
          agentId: event && event.agentId,
          agentUsername: event && event.agentUsername,
          timestamp: new Date().toISOString(),
        });

        // ============ PBX PLAN/SUBSCRIPTION CHECK ============
        // Ignore call status events if user doesn't have PBX plan or subscription expired
        const isPbxLocked = rootGetters.isPbxLocked;
        if (isPbxLocked) {
          console.log(
            "🚫 [PBX UI] *** PBX LOCKED - Ignoring call status event ***",
          );
          return;
        }
        // ============ END PBX PLAN CHECK ============

        if (!event) {
          console.warn("[PBX UI] ⚠️  Empty status event, ignoring");
          return;
        }

        // Check if this event is for this specific agent
        // If agentId is provided in the event, only process if it matches current user
        const currentUser = (rootState && rootState.user) || {};
        const currentUserId = currentUser.id;
        const eventAgentId = event.agentId;

        if (
          eventAgentId &&
          currentUserId &&
          String(eventAgentId) !== String(currentUserId)
        ) {
          console.log("[PBX UI] ⚠️  Status for different agent, ignoring:", {
            eventAgentId,
            currentUserId,
            status: event.status,
          });
          return;
        }

        // Check if this status update is for the current call
        // We need to match against either the incoming call ID or the outgoing SIP call ID
        const currentIncomingCallId =
          state.incomingCall && state.incomingCall.callId;
        const currentOutgoingSipCallId = state.sipCallId;
        const eventCallId = event.callId;

        // If we have an active call, check if this event is for that call
        if (eventCallId) {
          const isForCurrentIncoming =
            currentIncomingCallId && eventCallId === currentIncomingCallId;
          const isForCurrentOutgoing =
            currentOutgoingSipCallId &&
            (eventCallId === currentOutgoingSipCallId ||
              eventCallId.startsWith(currentOutgoingSipCallId) ||
              currentOutgoingSipCallId.startsWith(eventCallId));

          // If we have an active call but event is for different call, ignore it
          if (
            !isForCurrentIncoming &&
            !isForCurrentOutgoing &&
            (currentIncomingCallId || currentOutgoingSipCallId)
          ) {
            console.log("[PBX UI] ⚠️  Status for different call, ignoring:", {
              eventCallId,
              currentIncomingCallId,
              currentOutgoingSipCallId,
            });
            return;
          }

          // If we have NO active call and this is an error/end status, ignore it
          // This prevents stale events from affecting the UI
          if (!currentIncomingCallId && !currentOutgoingSipCallId) {
            const endStatuses = [
              "no-route",
              "failed",
              "busy",
              "cancelled",
              "timeout",
              "ended",
              "hangup",
              "agents-unavailable",
              "routing-error",
              "bridge-failed",
            ];
            const eventStatus = (event.status || "").toLowerCase();
            if (endStatuses.includes(eventStatus)) {
              console.log(
                "[PBX UI] ⚠️  End status but no active call, ignoring stale event:",
                {
                  eventCallId,
                  status: eventStatus,
                },
              );
              return;
            }
          }
        }

        const status = (event.status || "").toLowerCase();
        if (!status) {
          console.warn("[PBX UI] ⚠️  No status in event");
          return;
        }

        console.log("[PBX UI] 🔄 Updating call status:", status);

        if (["incoming", "routing", "ringing"].includes(status)) {
          // ============ BUSY CHECK - Don't show incoming call if already in a connected call ============
          // This prevents incoming call status updates from corrupting active call UI
          const isBusy =
            state.isInCall ||
            state.callStatus === "connected" ||
            (sessionInstance && !sessionInstance.isEnded()) ||
            (incomingSessionInstance && !incomingSessionInstance.isEnded());

          if (isBusy) {
            console.log(
              "📵 [PBX UI] *** BUSY - Ignoring incoming/ringing status update ***",
            );
            console.log("📵 [PBX UI] Current state:", {
              isInCall: state.isInCall,
              callStatus: state.callStatus,
              hasActiveSession: !!(
                sessionInstance && !sessionInstance.isEnded()
              ),
              hasActiveIncomingSession: !!(
                incomingSessionInstance && !incomingSessionInstance.isEnded()
              ),
            });
            return;
          }
          // ============ END BUSY CHECK ============

          console.log("[PBX UI] 🔔 Setting call to ringing state");
          commit("SET_INCOMING_STATUS", "ringing");
          commit("SET_CALL_STATUS", "ringing");
          // Show the popup for incoming calls
          commit("SET_INCOMING_VISIBLE", true);
          console.log("[PBX UI] 🔔 Incoming call popup should now be visible");
        } else if (
          ["bridged", "connected", "answered", "in-progress"].includes(status)
        ) {
          console.log(
            "[PBX UI] ✅ Call connected/bridged - keeping popup visible",
          );
          commit("SET_INCOMING_STATUS", "connected");
          commit("SET_CALL_STATUS", "connected");
          commit("SET_IN_CALL", true);
          // IMPORTANT: Keep popup visible during connected state
          commit("SET_INCOMING_VISIBLE", true);
        } else if (status === "agents-unavailable") {
          console.log("[PBX UI] ⚠️ Agents unavailable - failing call");
          commit("SET_INCOMING_STATUS", "failed");
          commit("SET_CALL_STATUS", "failed");
          // Close the popup after a short delay so user sees the failure
          setTimeout(() => {
            commit("SET_INCOMING_VISIBLE", false);
            commit("CLEAR_INCOMING_CALL");
            incomingSessionInstance = null;
          }, 3000);
        } else if (
          status === "caller-leg-completed" ||
          status === "agent-leg-completed" ||
          status === "hangup" ||
          status === "ended" ||
          status === "completed"
        ) {
          // CRITICAL: Caller hung up - force end the call on frontend
          console.log("[PBX UI] 📴 Call ended by remote party:", status);
          console.log("[PBX UI] 📴 Forcing call termination on frontend");

          // Terminate the JsSIP session if still active
          if (incomingSessionInstance && !incomingSessionInstance.isEnded()) {
            try {
              console.log(
                "[PBX UI] 📴 Terminating JsSIP session due to socket event",
              );
              incomingSessionInstance.terminate();
            } catch (e) {
              console.log("[PBX UI] ⚠️ Error terminating session:", e.message);
            }
          }
          if (
            sessionInstance &&
            sessionInstance !== incomingSessionInstance &&
            !sessionInstance.isEnded()
          ) {
            try {
              console.log(
                "[PBX UI] 📴 Terminating main session due to socket event",
              );
              sessionInstance.terminate();
            } catch (e) {
              console.log(
                "[PBX UI] ⚠️ Error terminating main session:",
                e.message,
              );
            }
          }

          stopCallTimer(commit);
          commit("SET_INCOMING_STATUS", "ended");
          commit("SET_IN_CALL", false);
          commit("SET_CALL_STATUS", "idle");
          commit("SET_INCOMING_VISIBLE", false);
          commit("SET_CURRENT_NUMBER", "");
          commit("CLEAR_INCOMING_AUDIO");
          setTimeout(() => {
            console.log(
              "[PBX UI] 🧹 Clearing incoming call data after remote hangup",
            );
            commit("CLEAR_INCOMING_CALL");
          }, 500);

          // Clear session references
          incomingSessionInstance = null;
          sessionInstance = null;
        } else if (
          [
            "failed",
            "busy",
            "cancelled",
            "timeout",
            "no-answer",
            "rejected",
            "no-route",
            "routing-error",
            "bridge-failed",
          ].includes(status)
        ) {
          // Only close popup for actual failure statuses
          console.log("[PBX UI] 📞 Call failed:", status);
          commit("SET_INCOMING_STATUS", "failed");
          commit("SET_IN_CALL", false);
          commit("SET_CALL_STATUS", "idle");
          commit("SET_INCOMING_VISIBLE", false);
          setTimeout(() => {
            console.log("[PBX UI] 🧹 Clearing incoming call data");
            commit("CLEAR_INCOMING_CALL");
            commit("CLEAR_INCOMING_AUDIO");
          }, 1500);
        } else {
          // For any other status (like "answered", "progress", etc.), just log it
          // Don't close the popup for unrecognized statuses
          console.log(
            "[PBX UI] 📞 Unhandled call status (keeping popup open):",
            status,
          );
        }
      });
    }

    if (socketInstance && socketInstance.connected) {
      const user = (rootState && rootState.user) || {};
      const companyId = user && user.companyId;
      if (companyId && String(companyId) !== String(joinedCompanyId)) {
        console.log("[Socket.io] 🔄 Re-joining company room:", companyId);
        socketInstance.emit("join-company", {
          companyId,
          agentId: user.id,
        });
        joinedCompanyId = companyId;
      }
    }

    try {
      // Build configuration
      const isHttps = window.location.protocol === "https:";
      const defaultPort = 5066;

      // For localhost development, always use WS regardless of HTTPS
      // For production, you may need to configure this differently
      const isLocalhost = domain === "127.0.0.1" || domain === "localhost";
      const defaultScheme = isHttps && !isLocalhost ? "wss" : "ws";

      // Use provided wsServers or fall back to auto-config
      // If wsServers is empty, use getFixedSipConfig() to get the correct default
      // This ensures the correct environment SIP server is used (no port) instead of :5066
      const envSipDomain = getFixedSipConfig().domain;
      const wsServer =
        wsServers ||
        (domain === envSipDomain
          ? getFixedSipConfig().wsServers
          : `${defaultScheme}://${domain}:${defaultPort}`);

      // IMPORTANT: The SIP URI domain MUST match what FreeSWITCH stores in registration
      // For incoming calls to work, the domain in registration must be consistent
      // Using userId (not email) avoids invalid URIs like "sip:user@gmail.com@domain"
      const sipUri = `sip:${sipUsername}@${domain}`;

      console.log("🔗 SIP Connection Attempt:", {
        domain,
        wsServer,
        sipUri,
        isHttps,
        isLocalhost,
        scheme: defaultScheme,
      });

      // Log warning if domain might cause issues
      if (isLocalhost) {
        console.warn(
          "⚠️  Using localhost domain - incoming calls may not route correctly",
        );
        console.warn(
          `⚠️  For production, set domain to your SIP server (e.g., ${envSipDomain})`,
        );
      }

      const iceServerList = iceServers
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((url) => ({ urls: url }));

      const socket = new JsSIP.WebSocketInterface(wsServer);

      // IMPORTANT: Force Via transport to "WS" even when connecting over wss://
      // This is required because nginx terminates TLS and FreeSWITCH only sees plain WS
      // Without this, JsSIP sends "Via: SIP/2.0/WSS" which FreeSWITCH doesn't respond to
      socket.via_transport = "WS";

      // Use user's actual name for display, fallback to username (email)
      const userDisplayName =
        displayName ||
        (user && user.name) ||
        (user && user.username) ||
        sipUsername;

      const configuration = {
        sockets: [socket],
        uri: sipUri,
        password: password,
        display_name: userDisplayName,
        register: true,
        rtcConstraints: {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        },
        pcConfig: {
          iceServers:
            iceServerList.length > 0
              ? iceServerList
              : [
                  { urls: "stun:127.0.0.1:3478" },
                  {
                    urls: "turn:127.0.0.1:3478",
                    username: "godial",
                    credential: "godial123",
                  },
                  { urls: "stun:stun.l.google.com:19302" },
                ],
          iceTransportPolicy: "all",
          iceCandidatePoolSize: 4,
          bundlePolicy: "max-bundle",
          rtcpMuxPolicy: "require",
        },
      };

      userAgentInstance = new JsSIP.UA(configuration);

      // Event handlers with debug logging
      userAgentInstance.on("connected", () => {
        console.log("✅ JsSIP: WebSocket connected");
        dispatch("addDebugLog", {
          level: "success",
          message: "WebSocket connected",
          details: { wsServer },
        });
        commit("SET_CONNECTION_STATUS", "connected");
      });

      userAgentInstance.on("disconnected", () => {
        console.log("🔌 JsSIP: WebSocket disconnected");
        dispatch("addDebugLog", {
          level: "warning",
          message: "WebSocket disconnected",
        });
        commit("SET_CONNECTION_STATUS", "disconnected");
      });

      userAgentInstance.on("registered", () => {
        console.log(
          "📋 JsSIP: Successfully registered - SIP calling now READY",
        );
        dispatch("addDebugLog", {
          level: "success",
          message: "SIP Registration successful",
          details: { sipUri },
        });
        commit("SET_CONNECTION_STATUS", "registered");
      });

      userAgentInstance.on("unregistered", () => {
        console.log("❌ JsSIP: Unregistered - SIP calling now UNAVAILABLE");
        dispatch("addDebugLog", { level: "info", message: "SIP Unregistered" });
        commit("SET_CONNECTION_STATUS", "connected");
      });

      userAgentInstance.on("registrationFailed", (e) => {
        console.error("❌ JsSIP: Registration failed:", e.cause);
        console.error("❌ JsSIP: Registration failure details:", {
          cause: e.cause,
          response: e.response && e.response.status_code,
          statusText: e.response && e.response.reason_phrase,
        });
        dispatch("addDebugLog", {
          level: "error",
          message: "SIP Registration failed",
          details: { cause: e.cause, sipUri },
        });

        // Show actionable error message based on failure cause
        const statusCode = e.response && e.response.status_code;
        let errorMsg = "SIP Registration Failed";
        if (
          e.cause === "Rejected" ||
          e.cause === "Authentication Error" ||
          statusCode === 403 ||
          statusCode === 401
        ) {
          errorMsg =
            "SIP login failed — please logout and login again to refresh your credentials.";
        } else if (e.cause === "Connection Error") {
          errorMsg = "Cannot reach SIP server — check your connection.";
        } else if (e.cause) {
          errorMsg = `SIP Registration Failed: ${e.cause}`;
        }

        commit("SET_CONNECTION_ERROR", errorMsg);
        commit("SET_CONNECTION_STATUS", "error");
      });

      // Add sipEvent listener to debug all SIP messages
      userAgentInstance.on("sipEvent", (e) => {
        console.log("[JsSIP] 📨 SIP Event:", e.event && e.event.method, e);
      });

      // Add newMessage listener for MESSAGE requests
      userAgentInstance.on("newMessage", (e) => {
        console.log("[JsSIP] 💬 New Message:", e);
      });

      userAgentInstance.on("newRTCSession", (e) => {
        const session = e.session;
        console.log("\n========== [JsSIP] NEW RTC SESSION ==========");
        console.log("[JsSIP] 📡 Direction:", session.direction);
        console.log("[JsSIP] 🆔 Session ID:", session._id);
        console.log(
          "[JsSIP] 📞 Call-ID:",
          session._request && session._request.call_id,
        );
        console.log("[JsSIP] 🕐 Timestamp:", new Date().toISOString());

        if (session.direction === "incoming") {
          console.log(
            "\n🔔🔔🔔 [JsSIP] *** INCOMING SIP SESSION RECEIVED *** 🔔🔔🔔",
          );

          // ============ PBX PLAN/SUBSCRIPTION CHECK ============
          // Block incoming calls if user doesn't have PBX plan or subscription expired
          const isPbxLocked = rootGetters.isPbxLocked;
          if (isPbxLocked) {
            console.log(
              "🚫 [JsSIP] *** PBX LOCKED - Rejecting incoming call (no PBX plan or subscription expired) ***",
            );
            try {
              // Reject with 403 Forbidden - subscription issue
              session.terminate({
                status_code: 403,
                reason_phrase: "Subscription Required",
              });
              console.log(
                "🚫 [JsSIP] Incoming call rejected with 403 Subscription Required",
              );
            } catch (rejectErr) {
              console.error(
                "🚫 [JsSIP] Failed to reject incoming call:",
                rejectErr.message,
              );
            }
            return;
          }
          // ============ END PBX PLAN CHECK ============

          // ============ BUSY CHECK - Auto-reject if already in a call ============
          // This prevents the active call UI from being overwritten by incoming call state
          // Check multiple conditions to be sure:
          // - state.isInCall: Vuex flag for in-call state
          // - state.callStatus === "connected": Currently connected to a call
          // - sessionInstance: Has an active outgoing call session
          // - incomingSessionInstance: Has an active incoming call session
          const isBusy =
            state.isInCall ||
            state.callStatus === "connected" ||
            sessionInstance ||
            incomingSessionInstance;

          if (isBusy) {
            console.log(
              "📵 [JsSIP] *** BUSY - Already in a call, auto-rejecting incoming call ***",
            );
            console.log("📵 [JsSIP] Current state:", {
              isInCall: state.isInCall,
              callStatus: state.callStatus,
              hasSession: !!sessionInstance,
              hasIncomingSession: !!incomingSessionInstance,
            });
            try {
              // Reject with 486 Busy Here - this tells the PBX to try other agents or play busy message
              session.terminate({
                status_code: 486,
                reason_phrase: "Busy Here",
              });
              console.log(
                "📵 [JsSIP] Incoming call rejected with 486 Busy Here",
              );
            } catch (rejectErr) {
              console.error(
                "📵 [JsSIP] Failed to reject incoming call:",
                rejectErr.message,
              );
            }
            console.log("[JsSIP] ========================================\n");
            return; // Don't process this incoming call further
          }
          // ============ END BUSY CHECK ============

          const identity = session.remote_identity || {};
          const uri = identity.uri ? identity.uri.toString() : "";
          const fromNumber = extractSipNumber(uri);
          const normalizedFrom = normalizeDialString(fromNumber);
          const displayName =
            identity.display_name || fromNumber || normalizedFrom;

          // Check if this is a real caller ID or anonymous/blocked
          const isRealCallerId =
            normalizedFrom &&
            !fromNumber.toLowerCase().includes("anonymous") &&
            !fromNumber.toLowerCase().includes("unavailable") &&
            !fromNumber.toLowerCase().includes("restricted");

          console.log("[JsSIP] 👤 Remote Identity:", identity);
          console.log("[JsSIP] 🌐 URI:", uri);
          console.log("[JsSIP] 📱 From Number (raw):", fromNumber);
          console.log("[JsSIP] 📱 From Number (normalized):", normalizedFrom);
          console.log("[JsSIP] 📱 Is Real Caller ID:", isRealCallerId);
          console.log("[JsSIP] 🏷️  Display Name:", displayName);
          console.log("[JsSIP] 📝 Full Request:", session._request);
          console.log(
            "[JsSIP] 📋 From Header:",
            session._request && session._request.from,
          );
          console.log(
            "[JsSIP] 📋 To Header:",
            session._request && session._request.to,
          );

          // Check if we already have incoming call data from socket event
          const existingCallData = state.incomingCall || {};

          // Determine the best caller ID to display:
          // 1. If SIP has a real number (not Anonymous), use it
          // 2. If socket has a real number but SIP is Anonymous, use socket data
          // 3. Otherwise fall back to whatever we have
          const socketFrom = existingCallData.from || "";
          const socketHasRealNumber =
            socketFrom &&
            !socketFrom.toLowerCase().includes("anonymous") &&
            !socketFrom.toLowerCase().includes("unavailable");

          // Pick the best caller ID
          let bestCallerId = "";
          let bestDisplayName = "";
          let isAnonymous = true;

          if (isRealCallerId) {
            // SIP has real caller ID - use it
            bestCallerId = normalizedFrom;
            bestDisplayName = displayName || normalizedFrom;
            isAnonymous = false;
            console.log("[JsSIP] 📱 Using caller ID from SIP:", bestCallerId);
          } else if (socketHasRealNumber) {
            // SIP is Anonymous but socket has real number - use socket
            bestCallerId = socketFrom;
            bestDisplayName =
              existingCallData.callerName ||
              existingCallData.displayName ||
              socketFrom;
            isAnonymous = false;
            console.log(
              "[JsSIP] 📱 Using caller ID from socket:",
              bestCallerId,
            );
          } else {
            // Both are Anonymous/Unavailable - use DID if available
            bestCallerId = existingCallData.did
              ? `Call via ${existingCallData.did}`
              : socketFrom || fromNumber || "Anonymous";
            bestDisplayName = existingCallData.did
              ? `Private (via ${existingCallData.did})`
              : "Private Caller";
            isAnonymous = true;
            console.log(
              "[JsSIP] 📱 Caller ID blocked/unavailable, using DID:",
              existingCallData.did,
            );
          }

          console.log("[JsSIP] 🔍 Existing call data check:", {
            hasExistingData: !!existingCallData.callId,
            existingCallId: existingCallData.callId,
            fromSocket: socketFrom,
            fromSIP: normalizedFrom,
            socketHasRealNumber,
            isRealCallerId,
            bestCallerId,
          });

          // IMPORTANT: Do NOT include 'session' in callData - it has circular refs
          // and will crash VuexPersistence when it tries to JSON.stringify
          // The session is stored separately in incomingSessionInstance
          const callData = {
            ...existingCallData, // Preserve socket event data (callId, strategy, etc.)
            // session, // REMOVED - causes "Converting circular structure to JSON" error
            from: bestCallerId,
            displayName: bestDisplayName,
            displayNumber: bestCallerId,
            isAnonymous: isAnonymous,
            direction: "incoming",
            timestamp: existingCallData.timestamp || new Date().toISOString(),
            sipCallId: session._request && session._request.call_id, // Store just the call ID string
          };

          // Store session in module-level variable (not in Vuex)
          incomingSessionInstance = session;

          console.log("[JsSIP] 💾 Setting incoming call state:", callData);
          commit("SET_INCOMING_CALL", callData);

          console.log("[JsSIP] 🎯 Setting UI state to RINGING");
          commit("SET_INCOMING_STATUS", "ringing");
          commit("SET_CALL_STATUS", "ringing");

          console.log("[JsSIP] 🔔 SHOWING INCOMING CALL DIALOG NOW!");
          commit("SET_INCOMING_VISIBLE", true);

          // Look up contact by phone number to display name during call (async, don't block)
          // Only lookup if we have a real (non-anonymous) caller ID
          if (!isAnonymous && bestCallerId) {
            console.log(
              "[JsSIP] 🔍 Looking up contact for incoming caller:",
              bestCallerId,
            );
            try {
              dispatch("lookupContactByPhone", bestCallerId).catch((err) =>
                console.warn(
                  "[JsSIP] Contact lookup failed (non-fatal):",
                  err.message,
                ),
              );
            } catch (err) {
              console.warn(
                "[JsSIP] Contact lookup dispatch failed (non-fatal):",
                err.message,
              );
            }
          } else {
            console.log(
              "[JsSIP] 📵 Skipping contact lookup - anonymous caller",
            );
          }

          console.log("[JsSIP] ✅ Session stored as incomingSessionInstance");
          console.log("[JsSIP] 🎨 Dialog should now be VISIBLE on screen!");
          console.log("[JsSIP] ========================================\n");

          if (incomingAudioElement) {
            console.log("[JsSIP] 🔊 Attaching remote audio (pre-accept)");
            attachRemoteAudio(session, incomingAudioElement);
          }

          session.on("accepted", () => {
            console.log(
              "[JsSIP] ✅ Session ACCEPTED event - keeping popup visible",
            );
            // Keep popup visible when call is accepted
            commit("SET_INCOMING_VISIBLE", true);
            if (incomingAudioElement) {
              attachRemoteAudio(session, incomingAudioElement);
            }
          });

          session.on("confirmed", () => {
            console.log("✅ [CONFIRMED] Incoming session confirmed!");
            console.log("✅ [CONFIRMED] Session state:", {
              status: session.status,
              direction: session.direction,
              id: session.id,
            });
            sessionInstance = session;
            commit("SET_INCOMING_STATUS", "connected");
            commit("SET_CALL_STATUS", "connected");
            commit("SET_IN_CALL", true);
            // Update agent status to busy so server knows not to route more calls to us
            dispatch("updateAgentSipStatus", "busy");
            // IMPORTANT: Keep popup visible during connected call
            commit("SET_INCOMING_VISIBLE", true);
            commit("SET_CURRENT_NUMBER", normalizedFrom || "");
            startCallTimer(commit);
            if (incomingAudioElement) {
              console.log("✅ [CONFIRMED] Attaching audio...");
              attachRemoteAudio(session, incomingAudioElement);
            }
          });

          session.on("ended", (data) => {
            console.log("📴 [ENDED] Incoming session ended");
            console.log("📴 [ENDED] End data:", data);

            // Update agent status back to available
            dispatch("updateAgentSipStatus", "available");

            // Save call data before clearing
            const callDuration = state.callDuration;
            const callNumber = state.currentNumber;
            const hasSipCallId = !!state.sipCallId;

            stopCallTimer(commit);
            commit("SET_IN_CALL", false);
            commit("SET_CALL_STATUS", "idle");
            commit("SET_INCOMING_STATUS", "ended");
            commit("SET_INCOMING_VISIBLE", false);
            commit("SET_CURRENT_NUMBER", "");
            commit("CLEAR_INCOMING_AUDIO");

            // Store last call data for disposition
            commit("SET_LAST_CALL_DATA", {
              duration: callDuration,
              number: callNumber,
              direction: "incoming", // CRITICAL: Mark as incoming call
            });

            setTimeout(() => {
              commit("CLEAR_INCOMING_CALL");
            }, 1200);

            if (sessionInstance === session) {
              sessionInstance = null;
            }
            incomingSessionInstance = null;

            // Show save contact dialog (if number unknown) then disposition dialog
            // Only if we have a SIP Call-ID (call was answered) and call duration > 0
            if (hasSipCallId && callDuration > 0) {
              console.log(
                "📋 [INCOMING] Checking if contact exists before disposition",
              );
              dispatch("checkAndShowSaveContactOrDisposition", {
                phone: callNumber,
                direction: "incoming",
              });
            }
          });

          session.on("failed", (event) => {
            console.log("❌ Incoming session failed", event && event.cause);
            // Update agent status back to available
            dispatch("updateAgentSipStatus", "available");
            stopCallTimer(commit);
            commit("SET_IN_CALL", false);
            commit("SET_CALL_STATUS", "failed");
            commit("SET_INCOMING_STATUS", "failed");
            commit("SET_INCOMING_VISIBLE", false);
            commit("CLEAR_INCOMING_AUDIO");
            setTimeout(() => {
              commit("CLEAR_INCOMING_CALL");
            }, 2000);
            if (sessionInstance === session) {
              sessionInstance = null;
            }
            incomingSessionInstance = null;
          });
        }
      });

      // Start UA and wait for registration with improved timeout handling
      return new Promise((resolve, reject) => {
        let wsConnectTimeout;
        let registrationTimeout;
        let isResolved = false;

        const cleanup = () => {
          if (wsConnectTimeout) clearTimeout(wsConnectTimeout);
          if (registrationTimeout) clearTimeout(registrationTimeout);
          if (userAgentInstance) {
            userAgentInstance.removeListener("connected", handleConnected);
            userAgentInstance.removeListener("registered", handleRegistered);
            userAgentInstance.removeListener(
              "registrationFailed",
              handleRegistrationFailed,
            );
            userAgentInstance.removeListener(
              "disconnected",
              handleDisconnected,
            );
          }
        };

        const finishWithError = (errorMsg) => {
          if (isResolved) return;
          isResolved = true;
          cleanup();
          commit("SET_CONNECTION_ERROR", errorMsg);
          commit("SET_CONNECTION_STATUS", "error");
          commit("SET_CONNECTION_PROGRESS", "");
          dispatch("addDebugLog", {
            level: "error",
            message: errorMsg,
          });
          // Stop the UA on error
          if (userAgentInstance) {
            try {
              userAgentInstance.stop();
            } catch (e) {
              /* ignore */
            }
            userAgentInstance = null;
          }
          reject(new Error(errorMsg));
        };

        const finishWithSuccess = () => {
          if (isResolved) return;
          isResolved = true;
          cleanup();
          connectionAbortController = null;
          commit("SET_CONNECTION_PROGRESS", "");
          resolve();
        };

        // Check for cancellation
        if (abortSignal.signal && abortSignal.signal.aborted) {
          finishWithError("Connection cancelled");
          return;
        }

        // WebSocket connection handler
        const handleConnected = () => {
          console.log("✅ JsSIP: WebSocket connected");
          if (wsConnectTimeout) clearTimeout(wsConnectTimeout);
          dispatch("addDebugLog", {
            level: "success",
            message: "WebSocket connected",
            details: { wsServer },
          });
          commit("SET_CONNECTION_STATUS", "connected");
          commit("SET_CONNECTION_PROGRESS", "Registering with SIP server...");

          // Start registration timeout (10 seconds after WebSocket connects)
          registrationTimeout = setTimeout(() => {
            finishWithError(
              "SIP registration timeout - server did not respond. Check your credentials.",
            );
          }, 10000);
        };

        // Success handler
        const handleRegistered = () => {
          console.log("✅ Registration successful!");
          finishWithSuccess();
        };

        // Failure handlers
        const handleRegistrationFailed = (e) => {
          const cause = e.cause || "Unknown error";
          let errorMsg = `Registration failed: ${cause}`;
          const statusCode = e.response && e.response.status_code;

          // Provide more helpful error messages
          if (cause.includes("Authentication")) {
            errorMsg = "Authentication failed - invalid credentials";
          } else if (cause.includes("Forbidden") || statusCode === 403) {
            errorMsg = "Access denied - check your account permissions";
          } else if (cause.includes("Not Found") || statusCode === 404) {
            errorMsg = "User not found on SIP server";
          }

          console.error("❌ Registration failed:", cause);
          finishWithError(errorMsg);
        };

        const handleDisconnected = () => {
          console.log("🔌 JsSIP: WebSocket disconnected");
          if (!isResolved) {
            finishWithError(
              "WebSocket disconnected - check server address and network",
            );
          }
        };

        // WebSocket connection timeout (8 seconds - fail fast if server unreachable)
        wsConnectTimeout = setTimeout(() => {
          if (state.connectionStatus === "connecting" && !isResolved) {
            finishWithError(
              "Connection timeout - cannot reach SIP server at " + wsServer,
            );
          }
        }, 8000);

        // Add listeners
        userAgentInstance.on("connected", handleConnected);
        userAgentInstance.once("registered", handleRegistered);
        userAgentInstance.once("registrationFailed", handleRegistrationFailed);
        userAgentInstance.on("disconnected", handleDisconnected);

        // Start the UserAgent
        console.log("🚀 Starting JsSIP UserAgent...");
        commit("SET_CONNECTION_PROGRESS", "Connecting to WebSocket...");
        userAgentInstance.start();
      });
    } catch (error) {
      console.error("❌ Connection failed:", error);
      commit("SET_CONNECTION_ERROR", error.message || "Connection failed");
      commit("SET_CONNECTION_STATUS", "error");
      commit("SET_CONNECTION_PROGRESS", "");
      throw error;
    }
  },

  disconnect({ commit, dispatch }) {
    // Cancel any pending connection
    if (connectionAbortController) {
      connectionAbortController.abort();
      connectionAbortController = null;
    }

    if (userAgentInstance) {
      userAgentInstance.stop();
      userAgentInstance = null;
    }
    commit("SET_CONNECTION_STATUS", "disconnected");
    commit("SET_CONNECTION_ERROR", null);
    commit("SET_CONNECTION_PROGRESS", "");
    commit("SET_CALL_STATUS", "idle");
    commit("SET_IN_CALL", false);

    dispatch("addDebugLog", {
      level: "info",
      message: "Disconnected from SIP server",
    });
  },

  async makeCall(
    { commit, state, dispatch, rootGetters, rootState },
    { targetNumber, audioElement, contactId = null, listId = null },
  ) {
    console.log(
      `[makeCall] Connection check: userAgentInstance=${!!userAgentInstance}, connectionStatus=${
        state.connectionStatus
      }, contactId=${contactId}, listId=${listId}`,
    );

    // ============ PBX PLAN/SUBSCRIPTION CHECK ============
    // Block outgoing calls if user doesn't have PBX plan or subscription expired
    const isPbxLocked = rootGetters.isPbxLocked;
    if (isPbxLocked) {
      console.error(
        "[makeCall] PBX locked - no active subscription or PBX plan",
      );
      throw new Error(
        "PBX subscription required. Please upgrade your plan to make calls.",
      );
    }
    // ============ END PBX PLAN CHECK ============

    // ============ PBX TRIAL CALL LIMIT CHECK ============
    try {
      const companyDetails = rootState.companyDetails;
      if (
        companyDetails &&
        companyDetails.plan &&
        companyDetails.plan.name === "Trial"
      ) {
        const endpoint = rootState.ENDPOINT || process.env.VUE_APP_ENDPOINT;
        const token = rootState.user && rootState.user.token;
        if (endpoint && token) {
          const limitResponse = await axios.get(
            `${endpoint}/eventMappingCaches/getPbxTrialCallCount`,
            {
              params: { companyId: companyDetails.id },
              headers: { Authorization: token },
            },
          );
          const limitData = limitResponse.data;
          if (limitData.isPbxCallLimitReached) {
            console.error("[makeCall] PBX trial call limit reached");
            Swal.fire({
              icon: "warning",
              title: "PBX Trial Limit Reached",
              text:
                limitData.message ||
                "Your PBX trial call limit has been reached. Please subscribe to continue using PBX calling.",
              confirmButtonText: "Subscribe Now",
              showCancelButton: true,
              cancelButtonText: "OK",
            }).then((result) => {
              if (result.value) {
                const app = document.querySelector("#app");
                if (app && app.__vue__ && app.__vue__.$router) {
                  app.__vue__.$router.push("/payments");
                }
              }
            });
            throw new Error("PBX trial call limit reached");
          }
        }
      }
    } catch (limitErr) {
      if (limitErr.message === "PBX trial call limit reached") {
        throw limitErr;
      }
      console.error("[makeCall] Error checking PBX trial limit:", limitErr);
    }
    // ============ END PBX TRIAL CALL LIMIT CHECK ============

    if (!userAgentInstance || state.connectionStatus !== "registered") {
      const statusMessages = {
        disconnected: "Not connected. Please wait for connection.",
        connecting: "Still connecting. Please wait a moment.",
        connected:
          "Connection established but SIP registration pending. Please wait.",
        error: "Registration failed. Please check your SIP credentials.",
      };
      const userMessage =
        statusMessages[state.connectionStatus] ||
        "Please wait for SIP registration.";
      const reason = !userAgentInstance
        ? "UserAgent not initialized"
        : `Status is '${state.connectionStatus}' (expected 'registered')`;
      console.error(`[makeCall] Cannot make call: ${reason}`);
      throw new Error(`Not connected to SIP server. ${userMessage}`);
    }

    if (state.isInCall) {
      throw new Error("Already in a call");
    }

    // Validate phone number format - must have country code (starts with +)
    const phoneNumber = targetNumber.trim();
    // if (!phoneNumber.startsWith("+") && !phoneNumber.includes("@")) {
    //   const errorMsg = `Invalid phone number format: "${phoneNumber}". Number must include country code (e.g., +1234567890)`;
    //   console.error(`📞 [makeCall] ${errorMsg}`);

    //   // For auto-dialer, just throw error - it will be caught and handled with proper popup + disposer
    //   // For manual calls, show an alert before throwing
    //   if (!state.isAutoDialing) {
    //     Swal.fire({
    //       icon: "warning",
    //       title: "Invalid Phone Number",
    //       html: `
    //         <p>Phone: <code>${phoneNumber}</code></p>
    //         <p class="text-danger mt-3">This number is missing a country code.</p>
    //         <p class="text-muted">Please add country code (e.g., +1, +91, etc.)</p>
    //       `,
    //       confirmButtonText: "OK",
    //       confirmButtonColor: "#ff9800",
    //     });
    //   }

    //   throw new Error(errorMsg);
    // }

    const { domain } = state.settings;
    const target = targetNumber.includes("@")
      ? targetNumber
      : `${targetNumber}@${domain}`;

    console.log(`📞 Making call to: ${target}`);
    commit("SET_CURRENT_NUMBER", targetNumber);

    // Store audioElement in a variable accessible by event handlers
    // If no audio element provided, try to get or create one
    let callAudioElement = audioElement;
    if (!callAudioElement) {
      console.log(
        "🔊 [makeCall] No audio element provided, looking for one...",
      );
      callAudioElement = document.getElementById("remote-audio");
      if (!callAudioElement) {
        console.log("🔊 [makeCall] Creating audio element");
        callAudioElement = document.createElement("audio");
        callAudioElement.id = "remote-audio";
        callAudioElement.autoplay = true;
        // Mobile-specific attributes for iOS Safari
        callAudioElement.setAttribute("playsinline", "");
        callAudioElement.setAttribute("webkit-playsinline", "");
        callAudioElement.setAttribute("x-webkit-airplay", "allow");
        callAudioElement.volume = 1.0;
        callAudioElement.style.display = "none";
        document.body.appendChild(callAudioElement);
      }
    }

    // Pre-warm audio element on mobile (this call happens from user interaction)
    if (isMobileBrowser()) {
      console.log("🔊 [makeCall] Mobile browser detected - pre-warming audio");
      await ensureAudioContextResumed();
      prewarmAudioElement(callAudioElement);
    }
    console.log(
      "🔊 [makeCall] Audio element ready:",
      !!callAudioElement,
      callAudioElement ? callAudioElement.id : "N/A",
    );

    const options = {
      eventHandlers: {
        peerconnection: (data) => {
          console.log("🔌 [OUTGOING] PeerConnection created for outgoing call");
          const pc = data.peerconnection;

          // Modern way to get remote audio - using ontrack event
          pc.ontrack = async (event) => {
            console.log(
              "🔊 [OUTGOING] Got remote track:",
              event.track.kind,
              "readyState:",
              event.track.readyState,
            );
            if (event.track.kind === "audio" && callAudioElement) {
              console.log("🔊 [OUTGOING] Attaching audio track to element");
              if (event.streams && event.streams[0]) {
                // Ensure AudioContext is resumed on mobile
                if (isMobileBrowser()) {
                  await ensureAudioContextResumed();
                }

                callAudioElement.srcObject = event.streams[0];
                callAudioElement.volume = 1.0;
                callAudioElement.muted = false;

                // Mobile-specific attributes
                callAudioElement.setAttribute("playsinline", "");
                callAudioElement.setAttribute("webkit-playsinline", "");

                // Retry play with exponential backoff for mobile
                const attemptPlay = async (maxRetries = 5) => {
                  for (let i = 0; i < maxRetries; i++) {
                    try {
                      await callAudioElement.play();
                      console.log(
                        `🔊 [OUTGOING] ✅ Audio playing (attempt ${i + 1})`,
                      );
                      return true;
                    } catch (e) {
                      console.warn(
                        `🔊 [OUTGOING] Play attempt ${i + 1} failed:`,
                        e.message,
                      );
                      if (i < maxRetries - 1) {
                        await new Promise((r) => setTimeout(r, 150 * (i + 1)));
                      }
                    }
                  }
                  console.warn(
                    "🔊 [OUTGOING] All play attempts failed - queuing for user interaction",
                  );
                  // Queue for play when user interacts with the page
                  queueAudioForPlay(callAudioElement);
                  return false;
                };

                attemptPlay();
              }
            }
          };

          // Monitor ICE connection state for debugging
          pc.oniceconnectionstatechange = () => {
            console.log("🧊 [OUTGOING] ICE state:", pc.iceConnectionState);
          };

          // Log local stream info for debugging microphone
          pc.onaddstream = (event) => {
            console.log(
              "🎤 [OUTGOING] Local stream added:",
              event.stream.getAudioTracks().length,
              "audio tracks",
            );
          };

          // Check getSenders to verify microphone is sending
          setTimeout(() => {
            const senders = pc.getSenders();
            senders.forEach((sender, i) => {
              if (sender.track) {
                console.log(
                  `📤 [OUTGOING] Sender ${i}: ${sender.track.kind} track, enabled: ${sender.track.enabled}, muted: ${sender.track.muted}`,
                );
              }
            });
          }, 2000);
        },
        progress: () => {
          console.log(
            `📞 JsSIP: Call in progress at ${new Date().toISOString()}`,
          );
          commit("SET_CALL_STATUS", "ringing");
        },
        confirmed: () => {
          console.log("✅ JsSIP: Call confirmed");
          commit("SET_CALL_STATUS", "connected");
          commit("SET_IN_CALL", true);

          // Update agent status to busy so server knows not to route more calls to us
          dispatch("updateAgentSipStatus", "busy");

          // Robust remote audio setup with multiple fallbacks
          const setupRemoteAudio = async () => {
            if (!sessionInstance || !sessionInstance.connection) {
              console.warn(
                "🔊 [OUTGOING] No session connection for audio setup",
              );
              return;
            }

            const pc = sessionInstance.connection;

            // Ensure we have an audio element
            let audioEl =
              callAudioElement || document.getElementById("remote-audio");
            if (!audioEl) {
              console.log("🔊 [OUTGOING] Creating fallback audio element");
              audioEl = document.createElement("audio");
              audioEl.id = "remote-audio-fallback";
              audioEl.autoplay = true;
              audioEl.setAttribute("playsinline", "");
              audioEl.setAttribute("webkit-playsinline", "");
              document.body.appendChild(audioEl);
            }

            // Resume AudioContext on mobile
            if (isMobileBrowser()) {
              await ensureAudioContextResumed();
            }

            // Try multiple methods to get remote audio
            let streamAttached = false;

            // Method 1: getReceivers (modern)
            if (!streamAttached && typeof pc.getReceivers === "function") {
              const receivers = pc.getReceivers();
              console.log(
                "📥 [OUTGOING] Checking receivers:",
                receivers.length,
              );
              const audioReceiver = receivers.find(
                (r) => r.track && r.track.kind === "audio",
              );
              if (audioReceiver && audioReceiver.track) {
                console.log(
                  "📥 [OUTGOING] Found audio receiver track, readyState:",
                  audioReceiver.track.readyState,
                );
                const stream = new MediaStream([audioReceiver.track]);
                audioEl.srcObject = stream;
                streamAttached = true;
                console.log("🔊 [OUTGOING] Audio attached via getReceivers");
              }
            }

            // Method 2: getRemoteStreams (deprecated but widely supported)
            if (!streamAttached && typeof pc.getRemoteStreams === "function") {
              const remoteStreams = pc.getRemoteStreams();
              console.log(
                "🔊 [OUTGOING] Remote streams count:",
                remoteStreams.length,
              );
              if (remoteStreams.length > 0) {
                audioEl.srcObject = remoteStreams[0];
                streamAttached = true;
                console.log(
                  "🔊 [OUTGOING] Audio attached via getRemoteStreams",
                );
              }
            }

            // Method 3: Check if srcObject was already set by ontrack
            if (!streamAttached && audioEl.srcObject) {
              console.log("🔊 [OUTGOING] srcObject already set (by ontrack)");
              streamAttached = true;
            }

            if (streamAttached) {
              audioEl.volume = 1.0;
              audioEl.muted = false;

              // Try to play with retries
              for (let attempt = 1; attempt <= 5; attempt++) {
                try {
                  await audioEl.play();
                  console.log(
                    `🔊 [OUTGOING] ✅ Audio playing on attempt ${attempt}`,
                  );

                  // Verify audio is actually playing
                  setTimeout(() => {
                    console.log("🔊 [OUTGOING] Audio element state:", {
                      paused: audioEl.paused,
                      muted: audioEl.muted,
                      volume: audioEl.volume,
                      readyState: audioEl.readyState,
                      srcObject: !!audioEl.srcObject,
                    });
                  }, 1000);

                  return;
                } catch (e) {
                  console.warn(
                    `🔊 [OUTGOING] Play attempt ${attempt} failed:`,
                    e.message,
                  );
                  await new Promise((r) => setTimeout(r, 200 * attempt));
                }
              }
              console.warn(
                "🔊 [OUTGOING] ⚠️ All play attempts failed - queuing for user interaction",
              );
              queueAudioForPlay(audioEl);
            } else {
              console.warn("🔊 [OUTGOING] ⚠️ No remote stream found!");
            }
          };

          // Run audio setup
          setupRemoteAudio();

          // Also run it again after a delay in case streams arrive late
          setTimeout(setupRemoteAudio, 1000);
          setTimeout(setupRemoteAudio, 3000);

          // Start call timer
          startCallTimer(commit);
        },
        ended: () => {
          console.log("📴 JsSIP: Call ended by remote");
          console.log(
            "📴 [ENDED] contactId from closure:",
            contactId,
            "listId:",
            listId,
          );

          // Update agent status back to available
          dispatch("updateAgentSipStatus", "available");

          // Save call data before clearing
          const callDuration = state.callDuration;
          const callNumber = state.currentNumber;
          const callContactName = state.currentContactName;

          commit("SET_CALL_STATUS", "idle");
          commit("SET_IN_CALL", false);
          commit("SET_SHOW_CALL_POPUP", false);
          commit("SET_CURRENT_NUMBER", "");
          commit("SET_CURRENT_CONTACT_NAME", "");
          // Only overwrite lastCallContactId if the closure has a value
          // If closure contactId is null/undefined, keep the pre-set value from auto-dialer
          if (contactId) {
            commit("SET_LAST_CALL_DATA", {
              duration: callDuration,
              number: callNumber,
              contactName: callContactName,
              contactId: contactId,
              listId: listId,
              direction: "outgoing",
            });
          } else {
            // Preserve existing contactId/listId (pre-set by auto-dialer)
            console.log(
              "📴 [ENDED] No contactId in closure, preserving pre-set values. Current lastCallContactId:",
              state.lastCallContactId,
            );
            commit("SET_LAST_CALL_DATA", {
              duration: callDuration,
              number: callNumber,
              contactName: callContactName,
              contactId: state.lastCallContactId,
              listId: state.lastCallListId,
              direction: "outgoing",
            });
          }

          stopCallTimer(commit);

          // Show disposition dialog if we have a SIP Call-ID or if auto-dialing
          // During auto-dialing, always show disposition to prevent getting stuck
          if (state.sipCallId || state.isAutoDialing) {
            commit("SET_SHOW_DISPOSITION_DIALOG", true);
          }

          sessionInstance = null;
        },
        failed: (e) => {
          const failedAt = new Date().toISOString();
          console.error(`❌ JsSIP: Call failed at ${failedAt}:`, e.cause);
          console.log(`⏱️ [TIMING] failed event received at: ${failedAt}`);

          // Check if this is a microphone/media access issue
          const isMediaAccessIssue =
            e.cause === "User Denied Media Access" ||
            e.cause === "NotFoundError" ||
            e.cause === "NotAllowedError" ||
            e.cause === "OverconstrainedError";

          if (isMediaAccessIssue) {
            console.error("🎤 MICROPHONE ACCESS ISSUE DETECTED:", e.cause);
            Swal.fire({
              type: "error",
              title: "Microphone Not Available",
              html: `
                <div style="text-align: left;">
                  <p style="margin-bottom: 16px;"><strong>Error:</strong> ${e.cause}</p>
                  <p style="margin-bottom: 12px;">Your browser cannot access the microphone. Please try the following:</p>
                  <ol style="padding-left: 20px; margin: 0 0 16px 0; line-height: 1.8;">
                    <li><strong>Disconnect and reconnect</strong> your microphone/headset</li>
                    <li>Click the <strong>lock icon 🔒</strong> in the address bar → Site Settings → Allow Microphone</li>
                    <li>Check <strong>System Settings → Privacy & Security → Microphone</strong> and enable your browser</li>
                    <li><strong>Restart your browser</strong> after granting permissions</li>
                    <li>Try using a <strong>different browser</strong> (Chrome recommended)</li>
                  </ol>
                  <p style="margin-top: 12px; padding: 10px; background: #f5f5f5; border-radius: 4px; font-size: 12px;">
                    <strong>💡 Quick Test:</strong> Open browser console (F12) and run:<br>
                    <code style="font-size: 11px;">navigator.mediaDevices.getUserMedia({audio:true})</code>
                  </p>
                </div>
              `,
              confirmButtonText: "Got it",
              confirmButtonColor: "#4CAF50",
            });
          }

          // Categorize the failure based on SIP status code and cause
          // JsSIP maps SIP status codes to cause strings:
          //   404 → "Not Found", 480 → "Temporarily Unavailable",
          //   486 → "Busy", 487 → "Request Terminated",
          //   500/502/504 → "Internal Error", 503 → "Service Unavailable",
          //   603 → "Rejected", 604 → "Does Not Exist", etc.
          const statusCode = e.status_code;
          const cause = e.cause || "";

          // Category 1: Carrier/PBX configuration issues (server-side errors)
          const isCarrierConfigIssue =
            (statusCode >= 500 && statusCode <= 504) ||
            cause === "Internal Error" ||
            cause === "Service Unavailable" ||
            cause === "SIP Failure Code";

          // Category 2: Invalid/wrong number
          const isInvalidNumber =
            cause === "Not Found" ||
            cause === "Does Not Exist" ||
            cause === "Address Incomplete" ||
            statusCode === 404 ||
            statusCode === 604 ||
            statusCode === 484;

          // Category 3: Call rejected/declined
          const isCallRejected =
            (cause === "Rejected" && statusCode !== 487) ||
            statusCode === 403 ||
            statusCode === 603;

          // Category 4: Busy
          const isBusy =
            cause === "Busy" || statusCode === 486 || statusCode === 600;

          // Category 5: No answer / temporarily unavailable
          const isUnavailable =
            cause === "No Answer" ||
            cause === "Temporarily Unavailable" ||
            statusCode === 408 ||
            statusCode === 480;

          // Category 6: Caller canceled (not an error)
          const isCanceled =
            cause === "Canceled" ||
            cause === "Request Terminated" ||
            statusCode === 487;

          // Category 7: Auth issue (could be carrier credential problem)
          const isAuthIssue =
            cause === "Authentication Error" ||
            statusCode === 401 ||
            statusCode === 407;

          // Category 8: Media/codec mismatch
          const isMediaIssue =
            cause === "Incompatible SDP" || statusCode === 488;

          // Build a user-friendly error message
          let userFriendlyError = "Call failed";
          let failureCategory = "unknown";
          if (isCarrierConfigIssue) {
            userFriendlyError = `Carrier error (${statusCode ||
              500}): ${e.reason_phrase || "Service Unavailable"}`;
            failureCategory = "carrier";
          } else if (isInvalidNumber) {
            userFriendlyError =
              "The number you dialed is invalid or does not exist";
            failureCategory = "invalid-number";
          } else if (isBusy) {
            userFriendlyError = "The number is busy";
            failureCategory = "busy";
          } else if (isUnavailable) {
            userFriendlyError = "The number is temporarily unavailable";
            failureCategory = "unavailable";
          } else if (isCallRejected) {
            userFriendlyError = "The call was rejected";
            failureCategory = "rejected";
          } else if (isCanceled) {
            userFriendlyError = "Call canceled";
            failureCategory = "canceled";
          } else if (isAuthIssue) {
            userFriendlyError =
              "Carrier authentication failed — check carrier credentials";
            failureCategory = "auth";
          } else if (isMediaIssue) {
            userFriendlyError =
              "Media negotiation failed — codec mismatch with carrier";
            failureCategory = "media";
          } else if (isMediaAccessIssue) {
            userFriendlyError = "Microphone not available";
            failureCategory = "microphone";
          } else {
            userFriendlyError = cause || "Call failed";
            failureCategory = "other";
          }

          console.log(`📞 Call failure category: ${failureCategory}`, {
            cause,
            statusCode,
            reasonPhrase: e.reason_phrase,
            userFriendlyError,
          });

          // Update agent status back to available
          dispatch("updateAgentSipStatus", "available");

          // Save call data before clearing
          const callDuration = state.callDuration;
          const callNumber = state.currentNumber;
          const callContactName = state.currentContactName;
          const hasSipCallId = !!state.sipCallId;

          console.log(
            `⏱️ [TIMING] About to close call popup at: ${new Date().toISOString()}`,
          );
          commit("SET_CALL_STATUS", "failed");
          commit("SET_CALL_ERROR", userFriendlyError);
          commit("SET_IN_CALL", false);
          commit("SET_SHOW_CALL_POPUP", false);
          console.log(
            `⏱️ [TIMING] Call popup closed at: ${new Date().toISOString()}`,
          );

          // Only overwrite lastCallContactId if the closure has a value
          if (contactId) {
            commit("SET_LAST_CALL_DATA", {
              duration: callDuration,
              number: callNumber,
              contactName: callContactName,
              contactId: contactId,
              listId: listId,
              direction: "outgoing",
            });
          } else {
            // Preserve existing contactId/listId (pre-set by auto-dialer)
            console.log(
              "❌ [FAILED] No contactId in closure, preserving pre-set values. Current lastCallContactId:",
              state.lastCallContactId,
            );
            commit("SET_LAST_CALL_DATA", {
              duration: callDuration,
              number: callNumber,
              contactName: callContactName,
              contactId: state.lastCallContactId,
              listId: state.lastCallListId,
              direction: "outgoing",
            });
          }
          stopCallTimer(commit);

          sessionInstance = null;

          // Show specific alert for carrier configuration issues
          // Only for actual server-side errors (5xx) or auth failures — NOT for invalid numbers, busy, etc.
          if (
            (isCarrierConfigIssue || isAuthIssue || isMediaIssue) &&
            state.isAutoDialing
          ) {
            Swal.fire({
              type: "error",
              title: "Call Failed",
              html: `
                <p style="margin-bottom: 16px;"><strong>Call failed with: ${statusCode ||
                  500} ${e.reason_phrase || "Service Unavailable"}</strong></p>
                <p style="margin-bottom: 16px;">This could mean your SIP carrier cannot route calls to this destination, or the carrier/trunk is not configured properly.</p>
                <p style="margin-bottom: 8px;"><strong>Possible reasons:</strong></p>
                <ol style="text-align: left; padding-left: 20px; margin: 0;">
                  <li>Carrier does not support calling this country/destination</li>
                  <li>Geo-permissions not enabled for this destination</li>
                  <li>Carrier credentials or configuration issue</li>
                  <li>Carrier does not allow outbound calls from your IP</li>
                </ol>
                <p style="margin-top: 12px; color: #666;">Click <strong>Dispose & Continue</strong> to mark this contact and move to the next call.</p>
              `,
              showCancelButton: true,
              confirmButtonText:
                '<i class="mdi mdi-phone-voip"></i> Stop & Go to PBX Settings',
              cancelButtonText: "Dispose & Continue",
              confirmButtonColor: "#4CAF50",
              cancelButtonColor: "#ff9800",
            }).then((result) => {
              if (result.value) {
                // Stop auto-dialer and redirect to PBX page
                dispatch("stopAutoDialer").then(() => {
                  if (window.$appRouter) {
                    window.$appRouter.push({ name: "GoDial PBX" });
                  }
                });
              } else {
                // User clicked "Dispose & Continue" — show disposition dialog so auto-dialer can continue
                console.log(
                  "[AUTO-DIALER] User chose to continue after carrier error, showing disposition dialog",
                );
                commit("SET_SHOW_DISPOSITION_DIALOG", true);
              }
            });
            // Don't fall through to avoid duplicate disposition dialog — it will be shown in the .then() callback
            return;
          }

          // For invalid numbers during auto-dialer, show a brief toast
          if (isInvalidNumber && state.isAutoDialing) {
            Swal.fire({
              icon: "warning",
              title: "Invalid Number",
              text: `"${callNumber}" is not a valid number. Please dispose to continue.`,
              timer: 3000,
              showConfirmButton: false,
              toast: true,
              position: "top-end",
            });
          }

          // For rejected calls during auto-dialer (e.g., carrier doesn't support this destination)
          if (isCallRejected && state.isAutoDialing) {
            Swal.fire({
              icon: "warning",
              title: "Call Rejected",
              text: `Call to "${callNumber}" was rejected. This destination may not be supported by your carrier.`,
              timer: 4000,
              showConfirmButton: false,
              toast: true,
              position: "top-end",
            });
          }

          // For busy/unavailable during auto-dialer, show a brief toast
          if ((isBusy || isUnavailable) && state.isAutoDialing) {
            Swal.fire({
              icon: "info",
              title: isBusy ? "Busy" : "No Answer",
              text: `"${callNumber}" is ${
                isBusy ? "busy" : "unavailable"
              }. Please dispose to continue.`,
              timer: 3000,
              showConfirmButton: false,
              toast: true,
              position: "top-end",
            });
          }

          // For manual calls (not auto-dialer), show user-friendly Swal for actionable errors
          if (!state.isAutoDialing && !isCanceled) {
            if (isInvalidNumber) {
              Swal.fire({
                icon: "warning",
                title: "Invalid Number",
                text: `The number "${callNumber}" is invalid or does not exist. Please check the number and try again.`,
                confirmButtonText: "OK",
                confirmButtonColor: "#ff9800",
              });
            } else if (isCallRejected) {
              Swal.fire({
                icon: "warning",
                title: "Call Rejected",
                html: `
                  <p style="margin-bottom: 16px;"><strong>Call to "${callNumber}" was rejected</strong></p>
                  <p>Your carrier may not support calling this destination. Check your carrier's geo-permissions or supported countries.</p>
                `,
                confirmButtonText: "OK",
                confirmButtonColor: "#ff9800",
              });
            } else if (isCarrierConfigIssue || isAuthIssue) {
              Swal.fire({
                type: "error",
                title: "Carrier Issue",
                html: `
                  <p style="margin-bottom: 16px;"><strong>Call failed with: ${statusCode ||
                    500} ${e.reason_phrase ||
                  "Service Unavailable"}</strong></p>
                  <p>Your SIP carrier/trunk may not be configured properly. Please check your PBX settings.</p>
                `,
                confirmButtonText: "OK",
                confirmButtonColor: "#f44336",
              });
            }
          }

          // Show disposition dialog for auto-dialer so user can track/skip
          // The disposition dialog closing will trigger the next call
          console.log(
            `⏱️ [TIMING] About to show disposition dialog at: ${new Date().toISOString()}`,
          );
          console.log(
            `⏱️ [TIMING] hasSipCallId: ${hasSipCallId}, isAutoDialing: ${state.isAutoDialing}`,
          );
          if (hasSipCallId || state.isAutoDialing) {
            commit("SET_SHOW_DISPOSITION_DIALOG", true);
            console.log(
              `⏱️ [TIMING] Disposition dialog shown at: ${new Date().toISOString()}`,
            );
          }
        },
        // Hold event - fired when call is placed on hold
        hold: (data) => {
          console.log("⏸️ [OUTGOING] Call hold event:", data.originator);
          if (data.originator === "local") {
            console.log(
              "⏸️ [OUTGOING] Successfully placed remote party on hold",
            );
          } else {
            console.log("⏸️ [OUTGOING] Remote party placed us on hold");
          }
        },
        // Unhold event - fired when call is resumed from hold
        unhold: (data) => {
          console.log("▶️ [OUTGOING] Call unhold event:", data.originator);
          if (data.originator === "local") {
            console.log("▶️ [OUTGOING] Successfully resumed call from hold");
          } else {
            console.log("▶️ [OUTGOING] Remote party resumed call from hold");
          }
        },
        // Reinvite event - monitor for hold-related re-INVITEs
        reinvite: (data) => {
          console.log("🔄 [OUTGOING] Re-INVITE received");
        },
      },
      mediaConstraints: {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      },
    };

    try {
      // Check if we're in localhost/development mode
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname.includes("192.168.") ||
        window.location.hostname.includes("10.0.");

      let hasMicrophone = false;
      let microphoneStream = null;

      // Check if mediaDevices API is available
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Try multiple audio constraint configurations
        const audioConstraintOptions = [
          // Try with all features enabled first
          {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
            video: false,
          },
          // Try with simpler constraints
          { audio: true, video: false },
          // Try with just audio, no video constraint
          { audio: true },
        ];

        for (const constraints of audioConstraintOptions) {
          try {
            console.log(
              "🎤 Requesting microphone access with constraints:",
              JSON.stringify(constraints),
            );
            microphoneStream = await navigator.mediaDevices.getUserMedia(
              constraints,
            );

            // If we got the stream, we have a microphone
            if (
              microphoneStream &&
              microphoneStream.getAudioTracks().length > 0
            ) {
              hasMicrophone = true;
              console.log("✅ Microphone access granted");
              console.log(
                `🎤 Audio tracks: ${microphoneStream.getAudioTracks().length}`,
              );

              // Update options to use the working constraints
              if (constraints.audio === true) {
                options.mediaConstraints = { audio: true, video: false };
              }

              // Stop the test stream - JsSIP will request it again
              microphoneStream.getTracks().forEach((track) => track.stop());
              microphoneStream = null;
              break; // Success, exit the loop
            }
          } catch (micError) {
            console.warn(
              "⚠️ Microphone access failed with constraints:",
              JSON.stringify(constraints),
              "-",
              micError.name,
              micError.message,
            );
            // Continue to next constraint option
          }
        }

        // If all constraint options failed, try to enumerate devices for debugging
        if (!hasMicrophone) {
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputDevices = devices.filter(
              (device) => device.kind === "audioinput",
            );

            console.log(
              `📋 Device enumeration found ${audioInputDevices.length} audio input device(s):`,
            );
            audioInputDevices.forEach((d, i) => {
              console.log(
                `   ${i + 1}. ${d.label ||
                  "Unnamed device"} (${d.deviceId.substring(0, 8)}...)`,
              );
            });

            // If no audio devices found at all, show helpful popup
            if (audioInputDevices.length === 0) {
              console.error("🎤 No microphone devices detected by browser");
              const result = await Swal.fire({
                type: "warning",
                title: "No Microphone Detected",
                html: `
                  <div style="text-align: left;">
                    <p style="margin-bottom: 12px;">Your browser cannot find any microphone. Please try:</p>
                    <ol style="padding-left: 20px; margin: 0 0 16px 0; line-height: 1.8;">
                      <li><strong>Connect a microphone</strong> or headset to your computer</li>
                      <li><strong>Disconnect and reconnect</strong> your current audio device</li>
                      <li>Check <strong>System Settings → Privacy & Security → Microphone</strong> and enable your browser</li>
                      <li><strong>Restart your browser</strong> after connecting the microphone</li>
                    </ol>
                    <p style="margin-top: 12px; color: #666; font-size: 13px;">
                      <strong>Note:</strong> On macOS, go to System Settings → Privacy & Security → Microphone and make sure your browser has permission.
                    </p>
                  </div>
                `,
                showCancelButton: true,
                confirmButtonText: "Try Call Anyway",
                cancelButtonText: "Cancel",
                confirmButtonColor: "#ff9800",
                cancelButtonColor: "#757575",
              });

              if (!result.value) {
                // User cancelled, don't proceed with call
                commit("SET_CALL_STATUS", "idle");
                return;
              }
            }

            // If devices exist but getUserMedia failed, try to proceed anyway
            // JsSIP might be able to access the microphone when making the actual call
            if (audioInputDevices.length > 0) {
              console.log(
                "🔄 Devices found but getUserMedia failed - will try to proceed with call anyway",
              );
              hasMicrophone = true; // Allow call to proceed
              options.mediaConstraints = { audio: true, video: false };
            }
          } catch (enumError) {
            console.warn("⚠️ Could not enumerate devices:", enumError.message);
          }
        }
      }

      // In localhost mode, allow calls without microphone (for testing)
      if (!hasMicrophone) {
        if (isLocalhost) {
          console.warn(
            "⚠️ No microphone available - running in TEST MODE (localhost)",
          );
          console.warn("📞 Call will be placed but you won't be able to speak");
          // Modify options to not require audio for the call to proceed
          options.mediaConstraints = { audio: false, video: false };
        } else {
          // Even in production, try to proceed with the call
          // The SIP stack might be able to handle audio differently
          console.warn(
            "⚠️ Microphone pre-check failed, but will attempt call anyway",
          );
          options.mediaConstraints = { audio: true, video: false };
          hasMicrophone = true; // Allow call to proceed
        }
      }

      console.log("📞 [CALL] Final call options:", {
        mediaConstraints: options.mediaConstraints,
        hasMicrophone: hasMicrophone,
        target: target,
      });

      sessionInstance = userAgentInstance.call(target, options);
      commit("SET_CALL_STATUS", "calling");
      commit("SET_CALL_DIRECTION", "outgoing");
      commit("SET_SHOW_CALL_POPUP", true);
      commit("SET_IN_CALL", true);

      // Close the dialer dialog when call starts - popup will show instead
      commit("SET_DIALER_OPEN", false);

      // Capture SIP Call-ID from session
      if (sessionInstance && sessionInstance.id) {
        const originalCallId = sessionInstance.id;

        // Truncate to 20 characters for RTPEngine compatibility
        // RTPEngine truncates Call-IDs to 20 chars in PCAP filenames
        const sipCallId = originalCallId.substring(0, 20);

        console.log(
          "🔍 JsSIP Original Call-ID:",
          originalCallId,
          `(${originalCallId.length} chars)`,
        );
        console.log(
          "✂️  Truncated Call-ID:",
          sipCallId,
          "(20 chars for RTPEngine)",
        );

        // Store truncated version consistently throughout the system
        commit("SET_SIP_CALL_ID", sipCallId);
        console.log("📞 SIP Call-ID stored:", sipCallId);
      }

      return sessionInstance;
    } catch (error) {
      console.error("❌ Call initiation failed:", error);

      // Provide user-friendly error messages with detailed popups
      let userMessage = error.message;
      let showDetailedPopup = false;
      let popupConfig = null;

      if (error.name === "NotFoundError") {
        userMessage =
          "No microphone found. Please connect a microphone or check your audio settings.";
        showDetailedPopup = true;
        popupConfig = {
          icon: "error",
          title: "Microphone Not Found",
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 16px;">Your browser cannot detect any microphone. This could be because:</p>
              <ul style="margin-bottom: 16px; padding-left: 20px;">
                <li>No microphone is connected</li>
                <li>Your browser doesn't have system permission to access microphones</li>
                <li>The microphone was disconnected</li>
              </ul>
              <p style="margin-bottom: 8px;"><strong>Try these steps:</strong></p>
              <ol style="padding-left: 20px; margin: 0;">
                <li><strong>Disconnect and reconnect</strong> your microphone/headset</li>
                <li>Check <strong>System Settings → Privacy & Security → Microphone</strong> and ensure your browser is enabled</li>
                <li><strong>Restart your browser</strong> after granting permission</li>
                <li>Try using a different microphone or headset</li>
              </ol>
            </div>
          `,
          confirmButtonText: "Got it",
          showCancelButton: true,
          cancelButtonText: "Retry",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#28a745",
        };
      } else if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        userMessage =
          "Microphone access denied. Please allow microphone access in your browser settings.";
        showDetailedPopup = true;
        popupConfig = {
          icon: "warning",
          title: "Microphone Access Denied",
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 16px;">You have denied microphone access for this site.</p>
              <p style="margin-bottom: 8px;"><strong>To fix this:</strong></p>
              <ol style="padding-left: 20px; margin: 0;">
                <li>Click the <strong>lock/info icon</strong> (🔒) in the address bar</li>
                <li>Click <strong>"Site settings"</strong></li>
                <li>Change <strong>Microphone</strong> from "Block" to "Allow"</li>
                <li><strong>Refresh the page</strong></li>
              </ol>
            </div>
          `,
          confirmButtonText: "Got it",
          showCancelButton: true,
          cancelButtonText: "Retry",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#28a745",
        };
      } else if (
        error.name === "NotReadableError" ||
        error.name === "TrackStartError"
      ) {
        userMessage =
          "Microphone is busy or unavailable. Please close other apps using the microphone.";
        showDetailedPopup = true;
        popupConfig = {
          icon: "warning",
          title: "Microphone Unavailable",
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 16px;">Your microphone is currently busy or cannot be accessed.</p>
              <p style="margin-bottom: 8px;"><strong>Try these steps:</strong></p>
              <ol style="padding-left: 20px; margin: 0;">
                <li><strong>Close other apps</strong> that might be using the microphone (Zoom, Teams, Discord, etc.)</li>
                <li><strong>Disconnect and reconnect</strong> your microphone</li>
                <li><strong>Refresh this page</strong> and try again</li>
              </ol>
            </div>
          `,
          confirmButtonText: "Got it",
          showCancelButton: true,
          cancelButtonText: "Retry",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#28a745",
        };
      } else if (error.name === "OverconstrainedError") {
        userMessage =
          "Microphone does not meet requirements. Please try a different microphone.";
      } else if (error.name === "SecurityError") {
        userMessage =
          "Microphone access blocked due to security settings. Please use HTTPS.";
        showDetailedPopup = true;
        popupConfig = {
          icon: "error",
          title: "Security Error",
          html: `
            <div style="text-align: left;">
              <p style="margin-bottom: 16px;">Microphone access is blocked due to security restrictions.</p>
              <p style="margin-bottom: 8px;"><strong>This usually happens when:</strong></p>
              <ul style="padding-left: 20px; margin: 0;">
                <li>The page is loaded over <strong>HTTP</strong> instead of <strong>HTTPS</strong></li>
                <li>The page is embedded in an iframe without proper permissions</li>
              </ul>
              <p style="margin-top: 16px;">Please ensure you're accessing this site via <strong>https://</strong></p>
            </div>
          `,
          confirmButtonText: "Got it",
          confirmButtonColor: "#3085d6",
        };
      }

      // Show detailed popup if configured
      if (showDetailedPopup && popupConfig) {
        Swal.fire(popupConfig).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel) {
            // User clicked "Retry" - they can try calling again after fixing the issue
            console.log("🔄 User will retry after fixing microphone issue");
          }
        });
      }

      commit("SET_CALL_STATUS", "failed");
      commit("SET_CALL_ERROR", userMessage);
      throw new Error(userMessage);
    }
  },

  /**
   * Toggle mute on the active call
   * Uses JsSIP's built-in mute/unmute for local audio
   * @param {boolean} muted - true to mute, false to unmute
   */
  toggleMute({ commit }, muted) {
    console.log("[MUTE] toggleMute action called with muted:", muted);
    const activeSession = sessionInstance || incomingSessionInstance;
    console.log(
      "[MUTE] activeSession:",
      !!activeSession,
      "sessionInstance:",
      !!sessionInstance,
      "incomingSessionInstance:",
      !!incomingSessionInstance,
    );

    if (!activeSession) {
      console.warn("[MUTE] No active session");
      return;
    }

    try {
      // Use JsSIP's built-in mute/unmute if available
      if (muted) {
        if (typeof activeSession.mute === "function") {
          activeSession.mute({ audio: true });
          console.log("[MUTE] ✅ Audio muted via session.mute()");
        } else {
          // Fallback to track manipulation
          const senders =
            activeSession.connection && activeSession.connection.getSenders();
          if (senders) {
            const audioSender = senders.find(
              (s) => s.track && s.track.kind === "audio",
            );
            if (audioSender && audioSender.track) {
              audioSender.track.enabled = false;
              console.log("[MUTE] ✅ Audio muted via track.enabled");
            }
          }
        }
      } else {
        if (typeof activeSession.unmute === "function") {
          activeSession.unmute({ audio: true });
          console.log("[MUTE] ✅ Audio unmuted via session.unmute()");
        } else {
          // Fallback to track manipulation
          const senders =
            activeSession.connection && activeSession.connection.getSenders();
          if (senders) {
            const audioSender = senders.find(
              (s) => s.track && s.track.kind === "audio",
            );
            if (audioSender && audioSender.track) {
              audioSender.track.enabled = true;
              console.log("[MUTE] ✅ Audio unmuted via track.enabled");
            }
          }
        }
      }
    } catch (error) {
      console.error("[MUTE] Error toggling mute:", error);
    }
  },

  /**
   * Toggle hold on the active call
   * Uses JsSIP's hold/unhold which sends re-INVITE
   * Falls back to mute/unmute if hold is not supported or fails
   * @param {boolean} held - true to hold, false to unhold
   */
  toggleHold({ commit, state }, held) {
    console.log("[HOLD] toggleHold action called with held:", held);
    const activeSession = sessionInstance || incomingSessionInstance;
    console.log(
      "[HOLD] activeSession:",
      !!activeSession,
      "sessionInstance:",
      !!sessionInstance,
      "incomingSessionInstance:",
      !!incomingSessionInstance,
    );

    if (!activeSession) {
      console.warn("[HOLD] No active session");
      return;
    }

    // Fallback function to use mute/unmute as pseudo-hold
    const useMuteAsFallback = () => {
      console.log("[HOLD] Using mute/unmute as fallback for hold");
      try {
        if (held) {
          if (typeof activeSession.mute === "function") {
            activeSession.mute({ audio: true });
            console.log("[HOLD] ✅ Call pseudo-held via mute");
          } else {
            // Fallback to track manipulation
            const pc = activeSession.connection;
            if (pc && pc.getSenders) {
              const senders = pc.getSenders();
              const audioSender = senders.find(
                (s) => s.track && s.track.kind === "audio",
              );
              if (audioSender && audioSender.track) {
                audioSender.track.enabled = false;
                console.log(
                  "[HOLD] ✅ Call pseudo-held via track.enabled = false",
                );
              }
            }
          }
        } else {
          if (typeof activeSession.unmute === "function") {
            activeSession.unmute({ audio: true });
            console.log("[HOLD] ✅ Call pseudo-unheld via unmute");
          } else {
            // Fallback to track manipulation
            const pc = activeSession.connection;
            if (pc && pc.getSenders) {
              const senders = pc.getSenders();
              const audioSender = senders.find(
                (s) => s.track && s.track.kind === "audio",
              );
              if (audioSender && audioSender.track) {
                audioSender.track.enabled = true;
                console.log(
                  "[HOLD] ✅ Call pseudo-unheld via track.enabled = true",
                );
              }
            }
          }
        }
      } catch (err) {
        console.error("[HOLD] Fallback mute/unmute failed:", err);
      }
    };

    try {
      // Check if session supports hold/unhold and is ready
      const supportsHold = typeof activeSession.hold === "function";
      const supportsUnhold = typeof activeSession.unhold === "function";
      const isReady =
        typeof activeSession.isReadyToReOffer !== "function" ||
        activeSession.isReadyToReOffer();

      console.log(
        "[HOLD] supportsHold:",
        supportsHold,
        "supportsUnhold:",
        supportsUnhold,
        "isReady:",
        isReady,
      );

      if (!supportsHold || !supportsUnhold) {
        console.warn(
          "[HOLD] Session doesn't support hold/unhold, using mute fallback",
        );
        useMuteAsFallback();
        return;
      }

      if (!isReady) {
        console.warn(
          "[HOLD] Session not ready for renegotiation, using mute fallback",
        );
        useMuteAsFallback();
        return;
      }

      if (held) {
        console.log("[HOLD] Attempting to place call on hold...");
        const result = activeSession.hold(null, () => {
          console.log("[HOLD] ✅ Hold renegotiation completed successfully");
        });

        if (result === false) {
          console.warn("[HOLD] ⚠️ hold() returned false - using mute fallback");
          useMuteAsFallback();
        } else {
          console.log("[HOLD] Hold request sent");
        }
      } else {
        console.log("[HOLD] Attempting to resume call from hold...");
        const result = activeSession.unhold(null, () => {
          console.log("[HOLD] ✅ Unhold renegotiation completed successfully");
        });

        if (result === false) {
          console.warn(
            "[HOLD] ⚠️ unhold() returned false - using unmute fallback",
          );
          useMuteAsFallback();
        } else {
          console.log("[HOLD] Unhold request sent");
        }
      }
    } catch (error) {
      console.error("[HOLD] Error toggling hold:", error);
      console.log("[HOLD] Falling back to mute/unmute");
      useMuteAsFallback();
    }
  },

  /**
   * Debug action to diagnose audio issues
   * Call this from browser console: store.dispatch('dialer/debugAudio')
   */
  async debugAudio({ state }) {
    console.log("=".repeat(60));
    console.log("🔊 AUDIO DEBUG INFORMATION");
    console.log("=".repeat(60));

    const activeSession = sessionInstance || incomingSessionInstance;

    // 1. Check session status
    console.log("\n📞 SESSION STATUS:");
    console.log("  - sessionInstance:", !!sessionInstance);
    console.log("  - incomingSessionInstance:", !!incomingSessionInstance);
    console.log("  - isInCall:", state.isInCall);
    console.log("  - callStatus:", state.callStatus);

    if (!activeSession) {
      console.log("  ❌ No active session!");
      return;
    }

    console.log("  - Session ID:", activeSession.id);
    console.log("  - Session status:", activeSession.status);

    // 2. Check PeerConnection
    const pc = activeSession.connection;
    console.log("\n🔌 PEER CONNECTION:");
    if (!pc) {
      console.log("  ❌ No PeerConnection!");
      return;
    }
    console.log("  - ICE state:", pc.iceConnectionState);
    console.log("  - Connection state:", pc.connectionState);
    console.log("  - Signaling state:", pc.signalingState);

    // 3. Check receivers (incoming audio)
    console.log("\n📥 RECEIVERS (remote audio coming IN):");
    if (typeof pc.getReceivers === "function") {
      const receivers = pc.getReceivers();
      console.log("  - Count:", receivers.length);
      receivers.forEach((r, i) => {
        if (r.track) {
          console.log(
            `  - Receiver ${i}: ${r.track.kind}, enabled=${r.track.enabled}, muted=${r.track.muted}, readyState=${r.track.readyState}`,
          );
        }
      });
    }

    // 4. Check senders (outgoing audio - your mic)
    console.log("\n📤 SENDERS (your audio going OUT):");
    if (typeof pc.getSenders === "function") {
      const senders = pc.getSenders();
      console.log("  - Count:", senders.length);
      senders.forEach((s, i) => {
        if (s.track) {
          console.log(
            `  - Sender ${i}: ${s.track.kind}, enabled=${s.track.enabled}, muted=${s.track.muted}, readyState=${s.track.readyState}`,
          );
        }
      });
    }

    // 5. Check remote streams
    console.log("\n🌊 REMOTE STREAMS:");
    if (typeof pc.getRemoteStreams === "function") {
      const streams = pc.getRemoteStreams();
      console.log("  - Count:", streams.length);
      streams.forEach((stream, i) => {
        const audioTracks = stream.getAudioTracks();
        console.log(`  - Stream ${i}: ${audioTracks.length} audio track(s)`);
        audioTracks.forEach((t, j) => {
          console.log(
            `    - Track ${j}: enabled=${t.enabled}, muted=${t.muted}, readyState=${t.readyState}`,
          );
        });
      });
    }

    // 6. Check audio elements
    console.log("\n🔈 AUDIO ELEMENTS:");
    const audioIds = [
      "remote-audio",
      "incoming-call-audio",
      "compact-dialer-audio",
      "remote-audio-fallback",
    ];
    audioIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        console.log(`  - #${id}:`);
        console.log(
          `    paused=${el.paused}, muted=${el.muted}, volume=${el.volume}`,
        );
        console.log(
          `    readyState=${el.readyState}, srcObject=${!!el.srcObject}`,
        );
        if (el.srcObject) {
          const tracks = el.srcObject.getAudioTracks();
          console.log(`    srcObject has ${tracks.length} audio track(s)`);
          tracks.forEach((t, i) => {
            console.log(
              `      Track ${i}: enabled=${t.enabled}, readyState=${t.readyState}`,
            );
          });
        }
      }
    });

    // 7. Try to fix audio
    console.log("\n🔧 ATTEMPTING TO FIX AUDIO...");

    // Resume AudioContext
    await ensureAudioContextResumed();
    console.log("  ✅ AudioContext resumed");

    // Find an audio element and attach stream
    const audioEl =
      document.getElementById("remote-audio") ||
      document.getElementById("incoming-call-audio") ||
      document.getElementById("compact-dialer-audio");

    if (audioEl && pc) {
      // Try getReceivers first
      if (typeof pc.getReceivers === "function") {
        const audioReceiver = pc
          .getReceivers()
          .find((r) => r.track && r.track.kind === "audio");
        if (audioReceiver) {
          const stream = new MediaStream([audioReceiver.track]);
          audioEl.srcObject = stream;
          audioEl.volume = 1.0;
          audioEl.muted = false;
          try {
            await audioEl.play();
            console.log("  ✅ Audio attached and playing via getReceivers!");
          } catch (e) {
            console.log("  ❌ Play failed:", e.message);
          }
        }
      }
    }

    console.log("\n" + "=".repeat(60));
    console.log("🔊 END AUDIO DEBUG");
    console.log("=".repeat(60));
  },

  /**
   * Force retry audio playback - call from UI if user can't hear
   * This should be called from a user gesture (button click) for best mobile compatibility
   */
  async forceRetryAudio({ state, dispatch }) {
    console.log("🔊 ==========================================");
    console.log("🔊 FORCE RETRY AUDIO PLAYBACK");
    console.log("🔊 ==========================================");

    const activeSession = sessionInstance || incomingSessionInstance;
    if (!activeSession) {
      console.warn("🔊 No active session found for audio retry");
      console.log("  sessionInstance:", !!sessionInstance);
      console.log("  incomingSessionInstance:", !!incomingSessionInstance);
      return false;
    }

    const pc = activeSession.connection;
    if (!pc) {
      console.warn("🔊 No PeerConnection on active session");
      return false;
    }

    console.log(
      "🔊 PeerConnection state:",
      pc.connectionState || pc.iceConnectionState,
    );

    // Step 1: Resume AudioContext (critical for mobile)
    await ensureAudioContextResumed();
    console.log("🔊 ✅ AudioContext resumed");

    // Step 2: Get audio receiver from PeerConnection
    let audioTrack = null;
    let audioStream = null;

    if (typeof pc.getReceivers === "function") {
      const receivers = pc.getReceivers();
      console.log("🔊 Found", receivers.length, "receivers");

      receivers.forEach((r, i) => {
        if (r.track) {
          console.log(
            `  Receiver ${i}: kind=${r.track.kind}, readyState=${r.track.readyState}, enabled=${r.track.enabled}`,
          );
          if (r.track.kind === "audio" && r.track.readyState === "live") {
            audioTrack = r.track;
          }
        }
      });
    }

    if (!audioTrack && typeof pc.getRemoteStreams === "function") {
      const streams = pc.getRemoteStreams();
      console.log("🔊 Found", streams.length, "remote streams");
      if (streams.length > 0) {
        audioStream = streams[0];
        const audioTracks = audioStream.getAudioTracks();
        if (audioTracks.length > 0) {
          audioTrack = audioTracks[0];
          console.log("🔊 Got audio track from getRemoteStreams");
        }
      }
    }

    if (!audioTrack) {
      console.error("🔊 ❌ No audio track found in PeerConnection!");
      console.log("  This could mean:");
      console.log("  - Remote side is not sending audio");
      console.log("  - RTPEngine/media proxy issue");
      console.log("  - Call not fully connected yet");
      return false;
    }

    console.log("🔊 ✅ Found audio track:", {
      kind: audioTrack.kind,
      readyState: audioTrack.readyState,
      enabled: audioTrack.enabled,
      muted: audioTrack.muted,
    });

    // Step 3: Create fresh MediaStream
    audioStream = audioStream || new MediaStream([audioTrack]);

    // Step 4: Try all audio elements
    const audioElements = [
      document.getElementById("remote-audio"),
      document.getElementById("incoming-call-audio"),
      document.getElementById("compact-dialer-audio"),
      document.getElementById("call-audio"),
      document.getElementById("remote-audio-fallback"),
      document.querySelector("audio[autoplay]"),
    ].filter(Boolean);

    console.log("🔊 Found", audioElements.length, "audio elements to try");

    let playSuccess = false;

    for (const audioEl of audioElements) {
      console.log(`🔊 Trying audio element: ${audioEl.id || "unnamed"}`);

      try {
        // Set the stream
        audioEl.srcObject = audioStream;
        audioEl.volume = 1.0;
        audioEl.muted = false;
        audioEl.autoplay = true;

        // Try to play
        await audioEl.play();

        console.log(
          `🔊 ✅ SUCCESS! Audio playing on ${audioEl.id || "unnamed"}`,
        );
        console.log(`🔊 Audio element state:`, {
          paused: audioEl.paused,
          volume: audioEl.volume,
          muted: audioEl.muted,
          readyState: audioEl.readyState,
          currentTime: audioEl.currentTime,
        });

        playSuccess = true;
        break;
      } catch (err) {
        console.warn(
          `🔊 ⚠️ Failed on ${audioEl.id || "unnamed"}:`,
          err.message,
        );
      }
    }

    // Step 5: If all existing elements failed, create a new one
    if (!playSuccess) {
      console.log("🔊 Creating new audio element as last resort...");

      const newAudio = new Audio();
      newAudio.id = "forced-audio-recovery";
      newAudio.srcObject = audioStream;
      newAudio.autoplay = true;
      newAudio.volume = 1.0;
      newAudio.muted = false;
      newAudio.setAttribute("playsinline", "");
      newAudio.setAttribute("webkit-playsinline", "");

      document.body.appendChild(newAudio);

      try {
        await newAudio.play();
        console.log("🔊 ✅ SUCCESS with newly created audio element!");
        playSuccess = true;
      } catch (err) {
        console.error("🔊 ❌ Even new audio element failed:", err.message);
        console.log("🔊 User may need to interact with the page first");
      }
    }

    console.log("🔊 ==========================================");
    console.log("🔊 FORCE RETRY RESULT:", playSuccess ? "SUCCESS" : "FAILED");
    console.log("🔊 ==========================================");

    return playSuccess;
  },

  /**
   * Send DTMF tone during an active call
   * @param {string} tone - DTMF digit (0-9, *, #)
   */
  sendDTMF({ commit }, tone) {
    const activeSession = sessionInstance || incomingSessionInstance;
    if (!activeSession) {
      console.warn("[DTMF] No active session");
      return;
    }

    try {
      if (typeof activeSession.sendDTMF === "function") {
        activeSession.sendDTMF(tone);
        console.log(`[DTMF] Sent tone: ${tone}`);
      } else {
        console.warn("[DTMF] Session does not support sendDTMF()");
      }
    } catch (error) {
      console.error("[DTMF] Error sending DTMF:", error);
    }
  },

  endCall({ commit, state, dispatch }) {
    console.log("🔴 [END CALL] Starting call termination");

    // Update agent status back to available
    dispatch("updateAgentSipStatus", "available");

    const activeSession = sessionInstance || incomingSessionInstance;
    console.log("🔴 [END CALL] Active session:", {
      hasSessionInstance: !!sessionInstance,
      hasIncomingSessionInstance: !!incomingSessionInstance,
      sessionStatus: activeSession ? activeSession.status : "no session",
      sessionId: activeSession ? activeSession.id : "no session",
      sessionDirection: activeSession ? activeSession.direction : "no session",
      sessionState: activeSession ? activeSession._state : "no session",
    });

    // Save call data before clearing
    const callDuration = state.callDuration;
    const callNumber = state.currentNumber;
    const hasSipCallId = !!state.sipCallId;
    const isIncomingCall = activeSession === incomingSessionInstance;

    if (activeSession) {
      try {
        console.log(
          "🔴 [END CALL] Session status before terminate:",
          activeSession.status,
        );
        console.log(
          "🔴 [END CALL] Session isEstablished:",
          activeSession.isEstablished && activeSession.isEstablished(),
        );
        console.log(
          "🔴 [END CALL] Session isEnded:",
          activeSession.isEnded && activeSession.isEnded(),
        );
        console.log("🔴 [END CALL] Calling terminate() on session");
        activeSession.terminate();
        console.log("🔴 [END CALL] terminate() called successfully");
      } catch (error) {
        console.warn("⚠️ [END CALL] Error terminating call:", error);
        console.error("⚠️ [END CALL] Error stack:", error.stack);
      }
    } else {
      console.warn("⚠️ [END CALL] No active session to terminate!");
    }

    if (activeSession && activeSession === incomingSessionInstance) {
      incomingSessionInstance = null;
      commit("SET_INCOMING_STATUS", "ended");
      commit("SET_INCOMING_VISIBLE", false);
      commit("CLEAR_INCOMING_AUDIO");
      setTimeout(() => {
        commit("CLEAR_INCOMING_CALL");
      }, 300);
    }

    sessionInstance = null;

    stopCallTimer(commit);
    commit("SET_CALL_STATUS", "idle");
    commit("SET_IN_CALL", false);
    commit("SET_SHOW_CALL_POPUP", false);
    commit("SET_CURRENT_NUMBER", "");
    commit("SET_LAST_CALL_DATA", {
      duration: callDuration,
      number: callNumber,
      direction: isIncomingCall ? "incoming" : "outgoing", // Track direction
    });

    // Show disposition dialog if we have a SIP Call-ID
    if (hasSipCallId) {
      commit("SET_SHOW_DISPOSITION_DIALOG", true);
    }
  },

  toggleDialer({ commit, state }) {
    commit("SET_DIALER_OPEN", !state.isDialerOpen);
  },

  openDialer({ commit, state }) {
    commit("SET_DIALER_OPEN", true);
    // Reset failed call state when opening dialer
    if (state.callStatus === "failed" && !state.isInCall) {
      commit("SET_CALL_STATUS", "idle");
      commit("SET_CURRENT_NUMBER", "");
    }
  },

  closeDialer({ commit, state }) {
    commit("SET_DIALER_OPEN", false);
    commit("SET_DIAL_PAD_VISIBLE", false);
    // Reset failed call state when closing dialer
    if (state.callStatus === "failed" && !state.isInCall) {
      commit("SET_CALL_STATUS", "idle");
      commit("SET_CURRENT_NUMBER", "");
    }
  },

  toggleDialPad({ commit, state }) {
    commit("SET_DIAL_PAD_VISIBLE", !state.showDialPad);
  },

  // Auto-dialer actions
  // Now uses /contacts/next API like the mobile app for proper contact locking
  startAutoDialer(
    { commit, state, dispatch, rootState },
    { contacts, listId },
  ) {
    if (state.connectionStatus !== "registered") {
      console.error("[AUTO-DIALER] Not connected to SIP server");
      throw new Error("Please connect to the SIP server first");
    }

    console.log(
      `[AUTO-DIALER] 🎯 startAutoDialer called with list ${listId} (${
        contacts && contacts.length ? contacts.length : 0
      } contacts passed)`,
    );
    console.log("[AUTO-DIALER] rootState.ENDPOINT:", rootState.ENDPOINT);
    console.log(
      "[AUTO-DIALER] rootState.user token exists:",
      !!(rootState.user && rootState.user.token),
    );

    // Clear any stale SIP call data from previous sessions
    console.log(
      "[AUTO-DIALER] 🧹 Clearing stale SIP call data before starting",
    );
    commit("CLEAR_SIP_CALL_DATA");

    // Clear any previous auto-dialer state
    commit("RESET_AUTO_DIALER");

    // Store the initial count for progress display
    commit(
      "SET_AUTO_DIALER_TOTAL_CONTACTS",
      contacts && contacts.length ? contacts.length : 0,
    );
    commit("SET_AUTO_DIALER_LIST_ID", listId);
    commit("SET_AUTO_DIALER_CURRENT_INDEX", 0);
    commit("SET_AUTO_DIALING", true);
    commit("SET_AUTO_DIALER_PAUSED", false);
    commit("SET_AUTO_DIALER_NO_MORE_CONTACTS", false);

    console.log(
      "[AUTO-DIALER] 📞 State set, now dispatching autoDialerNextCall...",
    );
    // Start fetching the first contact from API
    dispatch("autoDialerNextCall");
  },

  stopAutoDialer({ commit, dispatch, state }) {
    console.log("[AUTO-DIALER] Stopping auto-dialer");

    // If there's an active call, terminate it immediately
    if (state.isInCall) {
      console.log("[AUTO-DIALER] Terminating active call before stopping");
      dispatch("endCall");
    }

    // Clear auto-dialer state
    commit("RESET_AUTO_DIALER");

    // Also clear SIP call data to prevent stale contact IDs from persisting
    console.log(
      "[AUTO-DIALER] Clearing SIP call data to remove stale contact metadata",
    );
    commit("CLEAR_SIP_CALL_DATA");
  },

  pauseAutoDialer({ commit }) {
    console.log("[AUTO-DIALER] Pausing auto-dialer");
    commit("SET_AUTO_DIALER_PAUSED", true);
  },

  resumeAutoDialer({ commit, dispatch, state }) {
    console.log("[AUTO-DIALER] Resuming auto-dialer");
    commit("SET_AUTO_DIALER_PAUSED", false);

    // If not in a call, start the next call
    if (!state.isInCall && !state.showDispositionDialog) {
      dispatch("autoDialerNextCall");
    }
  },

  // Fetch next contact from API - same as mobile app
  async fetchNextContact({ state, rootState }) {
    const listId = state.autoDialerListId;
    if (!listId) {
      throw new Error("No list ID set for auto-dialer");
    }

    const endpoint = rootState.ENDPOINT || process.env.VUE_APP_ENDPOINT;
    const token = rootState.user && rootState.user.token;

    if (!token) {
      throw new Error("User not authenticated");
    }

    console.log(
      `[AUTO-DIALER] 📞 Calling /contacts/next API for list ${listId}`,
    );
    console.log(`[AUTO-DIALER] 📊 Current state before API call:`, {
      currentContactId:
        state.autoDialerCurrentContact && state.autoDialerCurrentContact.id,
      lastCallContactId: state.lastCallContactId,
      autoDialerIndex: state.autoDialerCurrentIndex,
    });

    try {
      const response = await axios.get(`${endpoint}/contacts/next`, {
        params: {
          listId: listId,
          dialerAction: "place_call",
          v2: 3,
        },
        headers: {
          Authorization: token,
        },
      });

      console.log(`[AUTO-DIALER] ✅ API Response:`, response.data);

      if (response.data.contact) {
        console.log(`[AUTO-DIALER] 📇 Contact details from API:`, {
          id: response.data.contact.id,
          phone: response.data.contact.phone,
          name: response.data.contact.name,
          listId: response.data.contact.listId,
          called: response.data.contact.called,
          dispo: response.data.contact.dispo,
        });

        // Warn if returning the same contact as before
        if (
          state.lastCallContactId &&
          response.data.contact.id === state.lastCallContactId
        ) {
          console.error(
            "🔴 [AUTO-DIALER] API RETURNED THE SAME CONTACT! This means the previous contact was not marked as called in the database.",
          );
          console.error("🔴 Previous contact ID:", state.lastCallContactId);
          console.error("🔴 Current contact ID:", response.data.contact.id);
        }
      }

      return response.data;
    } catch (error) {
      console.error(
        `[AUTO-DIALER] ❌ API Error:`,
        error.response ? error.response.data : error.message,
      );
      throw error;
    }
  },

  async autoDialerNextCall({ commit, state, dispatch, rootState }) {
    console.log("[AUTO-DIALER] 🚀 autoDialerNextCall called", {
      isAutoDialing: state.isAutoDialing,
      autoDialerPaused: state.autoDialerPaused,
      autoDialerListId: state.autoDialerListId,
      autoDialerNoMoreContacts: state.autoDialerNoMoreContacts,
    });

    if (!state.isAutoDialing || state.autoDialerPaused) {
      console.log("[AUTO-DIALER] Not active or paused, skipping next call");
      return;
    }

    // Check if we already know there are no more contacts
    if (state.autoDialerNoMoreContacts) {
      console.log("[AUTO-DIALER] No more contacts flag set, stopping");
      commit("RESET_AUTO_DIALER");
      return;
    }

    // Check PBX trial call limit before each call
    try {
      const companyDetails = rootState.companyDetails;
      if (
        companyDetails &&
        companyDetails.plan &&
        companyDetails.plan.name === "Trial"
      ) {
        const endpoint = rootState.ENDPOINT || process.env.VUE_APP_ENDPOINT;
        const token = rootState.user && rootState.user.token;
        if (endpoint && token) {
          const limitResponse = await axios.get(
            `${endpoint}/eventMappingCaches/getPbxTrialCallCount`,
            {
              params: { companyId: companyDetails.id },
              headers: { Authorization: token },
            },
          );
          const limitData = limitResponse.data;
          if (limitData.isPbxCallLimitReached) {
            console.log(
              "[AUTO-DIALER] ⚠️ PBX trial call limit reached, stopping auto-dialer",
            );
            commit("RESET_AUTO_DIALER");
            Swal.fire({
              icon: "warning",
              title: "PBX Trial Limit Reached",
              text:
                limitData.message ||
                "Your PBX trial call limit has been reached. Please subscribe to continue using PBX calling.",
              confirmButtonText: "Subscribe Now",
              showCancelButton: true,
              cancelButtonText: "OK",
            }).then((result) => {
              if (result.value) {
                // Navigate to payments page via Vue router
                const app = document.querySelector("#app");
                if (app && app.__vue__ && app.__vue__.$router) {
                  app.__vue__.$router.push("/payments");
                }
              }
            });
            return;
          }
        }
      }
    } catch (limitErr) {
      console.error("[AUTO-DIALER] Error checking PBX trial limit:", limitErr);
      // Don't block auto-dialer on limit check failure
    }

    try {
      console.log("[AUTO-DIALER] 📡 Fetching next contact from API...");
      // Fetch next contact from API (like mobile app does)
      const result = await dispatch("fetchNextContact");
      console.log("[AUTO-DIALER] 📦 fetchNextContact result:", result);

      if (!result.success || !result.contact) {
        console.log("[AUTO-DIALER] No more contacts available from API");
        commit("SET_AUTO_DIALER_NO_MORE_CONTACTS", true);

        // Show completion message
        Swal.fire({
          icon: "success",
          title: "Auto Dialer Complete",
          text: `All contacts have been called. Total: ${state.autoDialerCurrentIndex} calls made.`,
          timer: 3000,
          showConfirmButton: false,
        });

        commit("RESET_AUTO_DIALER");
        return;
      }

      const contact = result.contact;

      // Safety check: If API returns a contact that's already been called, skip it
      if (contact.called === true) {
        console.error(
          "🔴 [AUTO-DIALER] API returned a contact with called=true! Contact ID:",
          contact.id,
        );
        console.error(
          "🔴 This should not happen. Skipping and fetching next contact...",
        );
        // Increment index and try next contact
        commit("INCREMENT_AUTO_DIALER_INDEX");
        setTimeout(() => {
          dispatch("autoDialerNextCall");
        }, 1000);
        return;
      }

      const hasCountryCode =
        contact.phone && contact.phone.trim().startsWith("+");
      console.log(
        `[AUTO-DIALER] ✅ Got contact from API (call #${state.autoDialerCurrentIndex +
          1}):`,
        {
          id: contact.id,
          phone: contact.phone,
          name: contact.name,
          hasCountryCode: hasCountryCode,
          phoneFormat: hasCountryCode ? "✓ Valid" : "✗ Missing country code",
        },
      );

      // Store contact for tracking
      commit("SET_AUTO_DIALER_CURRENT_CONTACT", contact);
      commit("SET_CURRENT_CONTACT_NAME", contact.name || "");

      // CRITICAL: Pre-set lastCallContactId and lastCallListId in state immediately
      // so that disposition dialog always has them, even if makeCall's closure fails
      var contactListId = contact.listId || state.autoDialerListId;
      console.log(
        "[AUTO-DIALER] 🔒 Pre-setting lastCallContactId:",
        contact.id,
        "listId:",
        contactListId,
      );
      commit("SET_LAST_CALL_DATA", {
        duration: 0,
        number: contact.phone || "",
        contactName: contact.name || "",
        contactId: contact.id,
        listId: contactListId,
        direction: "outgoing",
      });

      // Small delay before placing call to ensure UI is ready
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Get audio element from the DOM - create one if it doesn't exist
      let audioElement = document.getElementById("remote-audio");

      if (!audioElement) {
        console.log(
          "[AUTO-DIALER] 🔊 Creating audio element (not found in DOM)",
        );
        audioElement = document.createElement("audio");
        audioElement.id = "remote-audio";
        audioElement.autoplay = true;
        audioElement.setAttribute("playsinline", "");
        audioElement.style.display = "none";
        document.body.appendChild(audioElement);
      }

      console.log("[AUTO-DIALER] 🔊 Audio element ready:", !!audioElement);

      await dispatch("makeCall", {
        targetNumber: contact.phone,
        audioElement: audioElement,
        contactId: contact.id, // Pass contact ID through to disposition
        listId: contact.listId || state.autoDialerListId, // Pass list ID through to disposition
      });
    } catch (error) {
      console.error("[AUTO-DIALER] Failed to fetch/call contact:", error);

      // Check if error is invalid phone number format
      const isInvalidPhoneFormat =
        error.message && error.message.includes("Invalid phone number format");

      // Check if error is "no more contacts"
      var errorMessage =
        error.response && error.response.data && error.response.data.message;
      var hasNoMoreContacts =
        (errorMessage && errorMessage.includes("No more NEW contacts")) ||
        (error.message && error.message.includes("No numbers to fetch"));

      if (hasNoMoreContacts) {
        console.log("[AUTO-DIALER] No more contacts available");
        commit("SET_AUTO_DIALER_NO_MORE_CONTACTS", true);

        Swal.fire({
          icon: "success",
          title: "Auto Dialer Complete",
          text: `All contacts have been called. Total: ${state.autoDialerCurrentIndex} calls made.`,
          timer: 3000,
          showConfirmButton: false,
        });

        commit("RESET_AUTO_DIALER");
        return;
      }

      // For invalid phone format, show popup and open disposer like a failed call
      if (isInvalidPhoneFormat) {
        console.log(
          "[AUTO-DIALER] ⚠️ Invalid phone format detected, opening disposer for manual handling",
        );

        // Get the current contact that failed
        const failedContact = state.autoDialerCurrentContact;

        if (failedContact) {
          // Generate a dummy SIP Call-ID for tracking (format: invalid-{timestamp}-{contactId})
          const dummySipCallId = `invalid-${Date.now()}-${failedContact.id ||
            "unknown"}`;

          console.log(
            "[AUTO-DIALER] Setting SIP Call ID for invalid number:",
            dummySipCallId,
          );
          console.log("[AUTO-DIALER] Failed contact details:", {
            id: failedContact.id,
            phone: failedContact.phone,
            name: failedContact.name,
            listId: failedContact.listId || state.autoDialerListId,
          });

          commit("SET_SIP_CALL_ID", dummySipCallId);

          // Set last call data so disposition dialog has contact info
          // CRITICAL: Include contactId and listId so disposition saves to the right contact
          commit("SET_LAST_CALL_DATA", {
            duration: 0, // No call was made
            number: failedContact.phone || "",
            direction: "outgoing",
            contactName: failedContact.name || "",
            contactId: failedContact.id || null, // Pass contact ID from API
            listId: failedContact.listId || state.autoDialerListId, // Pass list ID
          });

          // Show notification about invalid number
          Swal.fire({
            icon: "warning",
            title: "Invalid Phone Number",
            html: `
              <p><strong>${failedContact.name || "Contact"}</strong></p>
              <p>Phone: <code>${failedContact.phone || "N/A"}</code></p>
              <p class="text-danger mt-3">This number is missing a country code (e.g., +1, +91).</p>
              <p class="text-muted">Please mark a disposition to continue.</p>
            `,
            confirmButtonText: "OK, Open Disposer",
            confirmButtonColor: "#ff9800",
            allowOutsideClick: false,
          }).then(() => {
            // Open disposition dialog after user acknowledges
            commit("SET_SHOW_DISPOSITION_DIALOG", true);
          });
        } else {
          // Fallback if no contact stored - just skip
          console.warn(
            "[AUTO-DIALER] No contact stored for invalid number, skipping",
          );
          commit("INCREMENT_AUTO_DIALER_INDEX");
          setTimeout(() => {
            dispatch("autoDialerNextCall");
          }, 1000);
        }
        return;
      }

      // For other errors, increment index and try next after delay
      commit("INCREMENT_AUTO_DIALER_INDEX");
      setTimeout(() => {
        dispatch("autoDialerNextCall");
      }, 2000);
    }
  },

  // Called after disposition is submitted to continue auto-dialing
  autoDialerAfterDisposition({ commit, state, dispatch }) {
    if (!state.isAutoDialing) {
      console.log("[AUTO-DIALER] Not in auto-dial mode");
      return;
    }

    console.log(`[AUTO-DIALER] 🎯 Disposition completed. Auto-dialer state:`, {
      currentIndex: state.autoDialerCurrentIndex,
      totalContacts: state.autoDialerTotalContacts,
      currentContact: state.autoDialerCurrentContact,
      listId: state.autoDialerListId,
      lastCallContactId: state.lastCallContactId,
      lastCallListId: state.lastCallListId,
    });

    // Clear the last call contact ID and list ID now that disposition is done
    console.log("[AUTO-DIALER] 🧹 Clearing last call contact metadata");
    commit("SET_LAST_CALL_DATA", {
      duration: 0,
      number: "",
      contactName: "",
      contactId: null,
      listId: null,
      direction: "outgoing",
    });

    // Increment the count of calls made
    commit("INCREMENT_AUTO_DIALER_INDEX");
    commit("SET_AUTO_DIALER_CURRENT_CONTACT", null);

    console.log(`[AUTO-DIALER] ✅ Index incremented. New state:`, {
      newIndex: state.autoDialerCurrentIndex,
      callsCompleted: state.autoDialerCurrentIndex,
      totalContacts: state.autoDialerTotalContacts,
    });

    // Start 5 second countdown before fetching next contact
    commit("SET_AUTO_DIALER_COUNTDOWN", 5);
    console.log("[AUTO-DIALER] ⏱️ Starting 5-second countdown...");

    const countdownInterval = setInterval(() => {
      if (!state.isAutoDialing || state.autoDialerPaused) {
        console.log(
          "[AUTO-DIALER] ⏹️ Countdown stopped - auto-dialer inactive or paused",
        );
        clearInterval(countdownInterval);
        commit("SET_AUTO_DIALER_COUNTDOWN", 0);
        return;
      }

      commit("DECREMENT_AUTO_DIALER_COUNTDOWN");
      console.log(
        `[AUTO-DIALER] ⏱️ Countdown: ${state.autoDialerCountdown}s remaining`,
      );

      if (state.autoDialerCountdown <= 0) {
        clearInterval(countdownInterval);
        console.log(
          "[AUTO-DIALER] 🚀 Countdown complete! Fetching next contact...",
        );
        // Fetch next contact from API
        dispatch("autoDialerNextCall");
      }
    }, 1000);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
